import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  getQuote,
  getCandles,
  getIndicators,
  getStrategyChart,
  getAccount,
  getAccounts,
  getPositions,
  getTimeframeLadder,
  reconcilePositions,
  syncProtectiveStop as syncProtectiveStopApi,
  reProtectPosition as reProtectPositionApi,
  closePosition as closePositionApi,
  flatAll as flatAllApi,
  getTrades,
  getOrderDiagnostics,
  cancelOrder as cancelOrderApi,
  getRealtimeConfig,
  getAIAnalysis,
  getNewsEvents,
  patchAdoptExternalTrades,
} from "../services/api.trading";
import {
  getAutoTradeEffectiveState,
  getAutoTradeStatus,
  getAutoTradeRuns,
  setAiGuidedTrading,
  updateAlertPreferences,
  toggleAutoTradeScope,
  setAutoTradeScopeInterval,
  toggleAutoTrade,
  setAutoTradePaperMode,
  getAlerts,
  getSignalsAnalysis,
  toggleSignalScan,
  getStrategies,
  getCustomStrategies,
  getStrategyCatalog,
  getAccountStrategies,
  createAccountStrategy,
  toggleAccountStrategy,
  updateAccountStrategySettings,
  getDecisionTimeline,
} from "../services/api.strategies";
import { getBrokers, getPublicConfig } from "../services/api.broker";
import projectXWS from "../services/projectxWebSocket";
import { buildApiUrl } from "../services/apiBase";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "../components/ui/resizable";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  Zap,
  Clock,
  DollarSign,
  Target,
  AlertTriangle,
  Sparkles,
  RefreshCw,
  X,
  Wifi,
  WifiOff,
  Play,
  Pause,
  Bot,
  FileText,
  Bell,
  Check,
  SlidersHorizontal,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "../components/ui/drawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import { Switch } from "../components/ui/switch";
import CandlestickChart from "../components/CandlestickChart";
import PlatformShell from "../components/PlatformShell";
import StrategyRuntimeFields from "../components/StrategyRuntimeFields";
import AccountSnapshot from "../components/dashboard/AccountSnapshot";
import PositionsPanel from "../components/dashboard/PositionsPanel";
import OrderEntryPanel from "../components/dashboard/OrderEntryPanel";
import IndicatorsTab from "../components/dashboard/IndicatorsTab";
import SignalsTab from "../components/dashboard/SignalsTab";
import NewsTab from "../components/dashboard/NewsTab";
import AITab from "../components/dashboard/AITab";
import MarketSentiment from "../components/dashboard/MarketSentiment";
import DecisionLane from "../components/dashboard/DecisionLane";
import ChartStatusStrip from "../components/dashboard/ChartStatusStrip";
import ChartTrustIndicator from "../components/dashboard/ChartTrustIndicator";
import TimeframeStrip from "../components/dashboard/TimeframeStrip";
import { buildPositionChartData } from "../lib/positionChartOverlays";
import PriceLadder from "../components/dashboard/PriceLadder";
import AutoTradingControls from "../components/dashboard/AutoTradingControls";
import TradingTruthBar from "../components/dashboard/TradingTruthBar";
import MobileAccountScopeBar from "../components/dashboard/MobileAccountScopeBar";
import { useAuth } from "../context/AuthContext";
import { useOrderEntry } from "../hooks/useOrderEntry";
import useMediaQuery from "../hooks/useMediaQuery";
import { useMarketData } from "../hooks/useMarketData";
import {
  buildStrategyOptions,
  inferStrategyEngine,
  normalizeRuntimeDefinition,
  STRATEGY_ENGINES,
} from "../lib/strategyRuntime";
import { getApiErrorMessage } from "../lib/apiErrors";
import {
  getKnownInstrumentCatalog,
  instrumentOptionsFromBrokerCatalog,
  instrumentOptionsFromSymbols,
  normalizeInstrumentSymbol,
} from "../lib/instruments";
import {
  formatDateTimeDisplay,
  formatTimeDisplay,
  getBrowserTimezone,
  parseDateValue,
  resolveDisplayTimezone,
} from "../lib/datetime";
import { buildAutomationRunSignature } from "../lib/automationRunState";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  ComposedChart
} from "recharts";
const TIMEFRAMES = {
  "1m": 1,
  "2m": 2,
  "5m": 5,
  "15m": 15,
  "1h": 60,
  "4h": 240,
  "Day": 1440,
};

const MOBILE_DASHBOARD_SECTIONS = [
  { value: "chart", label: "Chart" },
  { value: "trade", label: "Trade" },
  { value: "positions", label: "Positions" },
  { value: "insights", label: "Insights" },
];

const MOBILE_INSIGHT_TABS = [
  { value: "signals", label: "Signals" },
  { value: "indicators", label: "Indicators" },
  { value: "news", label: "News" },
  { value: "ai", label: "AI" },
];

class DashboardSectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("DashboardSectionErrorBoundary caught:", error, info);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Card className="border-white/10 bg-white/[0.03]">
        <CardContent className="space-y-4 p-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">
              Temporary Rendering Issue
            </div>
            <div className="mt-2 text-base font-semibold text-white">
              This panel hit a temporary error.
            </div>
            <div className="mt-1 text-sm text-white/60">
              Try again below. You should not need to leave the page or refresh the whole app.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => this.setState({ hasError: false, error: null })}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
}

const QUOTE_STALE_MS = 10000;
const DASHBOARD_BOOT_TIMEOUT_MS = 6000;
const DEFAULT_MARKET_REFRESH_MS = 5000;
const DEFAULT_SNAPSHOT_REFRESH_MS = 5000;
const DEFAULT_ACCOUNT_REFRESH_MS = 20000;
const DEFAULT_POSITION_MARK_REFRESH_MS = 5000;
const DEFAULT_ORDER_DIAGNOSTICS_REFRESH_MS = 15000;
const ACTIVE_POSITION_QUOTE_REFRESH_MS = 250;
const ACTIVE_POSITION_SNAPSHOT_REFRESH_MS = 1000;
const ACTIVE_POSITION_ACCOUNT_REFRESH_MS = 5000;
const ACTIVE_POSITION_MARK_REFRESH_MS = 1000;
const LIVE_MARK_MAX_DRIFT_RATIO = 0.2;
const BROWSER_TIMEZONE = getBrowserTimezone();
const TEMPORARY_TOPSTEP_BAR_COUNTS = {
  1: 300,
  2: 300,
  5: 300,
  15: 300,
  60: 300,
  240: 300,
  1440: 300,
};

const getDashboardPollIntervalMs = (value, fallback = 5000) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const KNOWN_SYMBOL_PREFIXES = getKnownInstrumentCatalog()
  .map((instrument) => instrument.value)
  .sort((left, right) => right.length - left.length);
const TRADOPRO_CORE_SYMBOLS = getKnownInstrumentCatalog().map((instrument) => instrument.value);

const getTemporaryChartBarCount = (intervalMinutes) => {
  const safeInterval = Number(intervalMinutes) || 5;
  return TEMPORARY_TOPSTEP_BAR_COUNTS[safeInterval] || 300;
};

const getStickyMarketDataFloor = (intervalMinutes) => {
  const safeInterval = Number(intervalMinutes) || 5;
  if (safeInterval <= 2) return 20;
  if (safeInterval <= 5) return 12;
  if (safeInterval <= 15) return 8;
  if (safeInterval <= 60) return 4;
  return 1;
};

const getMarketDataScopeKey = (rawSymbol, intervalMinutes) => {
  const normalizedSymbol = normalizeInstrumentSymbol(rawSymbol) || String(rawSymbol || "").trim() || "unknown";
  const safeInterval = Number(intervalMinutes) || 5;
  return `${normalizedSymbol}:${safeInterval}`;
};

const getOrderDiagnosticsScopeKey = (accountId, rawSymbol) => {
  const normalizedSymbol = normalizeInstrumentSymbol(rawSymbol) || String(rawSymbol || "").trim() || "unknown";
  return `${String(accountId || "none").trim() || "none"}::${normalizedSymbol}`;
};

const isCompatibleLivePrice = (candidatePrice, referencePrice) => {
  const candidate = Number(candidatePrice);
  const reference = Number(referencePrice);
  if (!Number.isFinite(candidate) || candidate <= 0) {
    return false;
  }
  if (!Number.isFinite(reference) || reference <= 0) {
    return true;
  }
  return Math.abs(candidate - reference) / reference <= LIVE_MARK_MAX_DRIFT_RATIO;
};

const inferSymbolFromRaw = (rawSymbol) => {
  const normalized = normalizeInstrumentSymbol(rawSymbol);
  if (!normalized) return "";
  for (const knownSymbol of KNOWN_SYMBOL_PREFIXES) {
    if (normalized === knownSymbol || normalized.startsWith(knownSymbol)) {
      return knownSymbol;
    }
  }
  return normalized;
};

const linkMatchesSelectedSymbol = (link, selectedSymbol) => {
  const normalizedSelected = normalizeInstrumentSymbol(selectedSymbol);
  if (!normalizedSelected) return false;

  const candidates = [
    link?.primary_symbol,
    link?.broker_contract_label,
    ...(Array.isArray(link?.instruments) ? link.instruments : []),
  ];

  for (const rawCandidate of candidates) {
    const candidate = inferSymbolFromRaw(rawCandidate);
    if (!candidate) continue;
    if (candidate === normalizedSelected) return true;
  }
  return false;
};

// Contract ID mapping for ProjectX
const CONTRACT_MAP = {
  "ES": "CON.F.US.EP",
  "NQ": "CON.F.US.ENQ",
  "MES": "CON.F.US.MES",
  "MNQ": "CON.F.US.MNQ",
  "CL": "CON.F.US.CLE",
  "MCL": "CON.F.US.MCLE",
  "GC": "CON.F.US.GCE",
  "MGC": "CON.F.US.MGC",
  "SI": "CON.F.US.SIE",
  "SIL": "CON.F.US.SIL",
  "RTY": "CON.F.US.RTY",
  "YM": "CON.F.US.YM",
};

const DASHBOARD_TYPOGRAPHY = {
  sectionTitle: "text-xs font-semibold uppercase tracking-[0.18em] text-white/65",
  value: "text-2xl font-semibold tracking-tight text-white",
  body: "text-sm font-medium text-white",
  meta: "text-sm text-white/55",
  fine: "text-xs text-white/60",
};

const formatSignalBadgeLabel = (value) => {
  if (!value) return "Neutral";
  const normalized = String(value).toUpperCase();
  if (normalized === "BUY") return "Buy";
  if (normalized === "SELL") return "Sell";
  if (normalized === "EXIT" || normalized === "CLOSE") return "Exit";
  return normalized.charAt(0) + normalized.slice(1).toLowerCase();
};

const formatMarketDataSourceLabel = (value) => {
  if (!value) return "Unknown";
  const normalized = String(value).toLowerCase();
  if (normalized === "historical_store") return "Historical Store";
  if (normalized === "historical_store_partial") return "Historical Store (Partial)";
  if (normalized === "historical_store_with_live_tail") return "Historical Store + Live Tail";
  if (normalized === "topstepx") return "TopstepX";
  if (normalized === "topstepx_with_store_backfill") return "TopstepX + Store Backfill";
  if (normalized === "simulated") return "Simulated";
  if (normalized === "fetch_failed") return "Fetch Failed";
  if (normalized === "topstepx_unavailable") return "TopstepX Unavailable";
  if (normalized === "store_unavailable") return "Store Unavailable";
  if (normalized === "auth_failed") return "Auth Failed";
  if (normalized === "error") return "Error";
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const isMarketDataErrorSource = (value) => {
  if (!value) return false;
  const normalized = String(value).toLowerCase();
  return (
    normalized === "error"
    || normalized === "fetch_failed"
    || normalized === "topstepx_unavailable"
    || normalized === "store_unavailable"
    || normalized === "auth_failed"
  );
};

const formatDataQualityLabel = (dataQuality) => {
  if (!dataQuality) return "Quality unknown";
  if (dataQuality.freshness === "stale") {
    return dataQuality.is_live ? "Live quote stale" : "Historical stale";
  }
  if (dataQuality.is_live) {
    return dataQuality.prefer_live ? "Broker-aligned live" : "Live";
  }
  return "Historical";
};

const dataQualityBadgeClassName = (dataQuality) => {
  if (!dataQuality) {
    return "border-white/10 bg-white/5 text-white/70";
  }
  if (dataQuality.freshness === "stale") {
    return "border-amber-500/30 bg-amber-500/15 text-amber-200";
  }
  if (dataQuality.is_live) {
    return "border-emerald-500/30 bg-emerald-500/15 text-emerald-200";
  }
  return "border-cyan-500/30 bg-cyan-500/15 text-cyan-200";
};

const shortenParitySignature = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  return text ? text.slice(0, 8) : null;
};

const formatChartTimestamp = (value, timezone = BROWSER_TIMEZONE) => {
  if (!value) return "—";
  return formatDateTimeDisplay(value, timezone, { timeZoneName: "short" }) || String(value);
};

const normalizeQuoteTimestamp = (value) => {
  const parsed = parseDateValue(value);
  const ms = parsed?.getTime();
  return Number.isFinite(ms) ? ms : null;
};

const appendQuoteTailToCandles = ({
  candles,
  recentQuotes,
  timeframe,
  source,
  wsConnected,
}) => {
  const baseCandles = Array.isArray(candles) ? candles : [];
  const baseSource = source || "unknown";

  if (!wsConnected || !recentQuotes?.length || !baseCandles.length) {
    return {
      candles: baseCandles,
      source: baseSource,
      tailApplied: false,
      firstTimestamp: baseCandles[0]?.timestamp || null,
      lastTimestamp: baseCandles[baseCandles.length - 1]?.timestamp || null,
      count: baseCandles.length,
    };
  }

  const lastBase = baseCandles[baseCandles.length - 1];
  const lastBaseMs = normalizeQuoteTimestamp(lastBase?.timestamp);
  const lastBaseClose = Number(lastBase?.close || 0);
  if (!Number.isFinite(lastBaseMs) || !Number.isFinite(lastBaseClose) || lastBaseClose <= 0) {
    return {
      candles: baseCandles,
      source: baseSource,
      tailApplied: false,
      firstTimestamp: baseCandles[0]?.timestamp || null,
      lastTimestamp: baseCandles[baseCandles.length - 1]?.timestamp || null,
      count: baseCandles.length,
    };
  }

  const quotePoints = recentQuotes
    .map((quote) => ({
      ts: normalizeQuoteTimestamp(quote.time),
      price: Number(quote.price || 0),
    }))
    .filter((quote) => Number.isFinite(quote.ts) && Number.isFinite(quote.price) && quote.price > 0)
    .sort((left, right) => left.ts - right.ts);

  if (!quotePoints.length) {
    return {
      candles: baseCandles,
      source: baseSource,
      tailApplied: false,
      firstTimestamp: baseCandles[0]?.timestamp || null,
      lastTimestamp: baseCandles[baseCandles.length - 1]?.timestamp || null,
      count: baseCandles.length,
    };
  }

  const latestQuote = quotePoints[quotePoints.length - 1];
  if (latestQuote.ts <= lastBaseMs) {
    return {
      candles: baseCandles,
      source: baseSource,
      tailApplied: false,
      firstTimestamp: baseCandles[0]?.timestamp || null,
      lastTimestamp: baseCandles[baseCandles.length - 1]?.timestamp || null,
      count: baseCandles.length,
    };
  }

  const priceRatio = latestQuote.price / lastBaseClose;
  if (!Number.isFinite(priceRatio) || priceRatio < 0.5 || priceRatio > 1.5) {
    return {
      candles: baseCandles,
      source: baseSource,
      tailApplied: false,
      firstTimestamp: baseCandles[0]?.timestamp || null,
      lastTimestamp: baseCandles[baseCandles.length - 1]?.timestamp || null,
      count: baseCandles.length,
    };
  }

  const intervalMinutes = TIMEFRAMES[timeframe] || 5;
  const bucketMs = intervalMinutes * 60 * 1000;
  const buckets = new Map();
  const anchorBucketStart = lastBaseMs;

  quotePoints.forEach((quote) => {
    if (quote.ts < anchorBucketStart) {
      return;
    }
    const bucketStart = anchorBucketStart + Math.floor((quote.ts - anchorBucketStart) / bucketMs) * bucketMs;
    const existing = buckets.get(bucketStart);
    if (!existing) {
      const seededFromBase =
        bucketStart === lastBaseMs
          ? {
              timestamp: lastBase.timestamp,
              open: Number(lastBase.open),
              high: Number(lastBase.high),
              low: Number(lastBase.low),
              close: Number(lastBase.close),
              volume: Number(lastBase.volume || 0),
            }
          : null;

      if (seededFromBase) {
        seededFromBase.high = Math.max(seededFromBase.high, quote.price);
        seededFromBase.low = Math.min(seededFromBase.low, quote.price);
        seededFromBase.close = quote.price;
        buckets.set(bucketStart, seededFromBase);
        return;
      }

      buckets.set(bucketStart, {
        timestamp: new Date(bucketStart).toISOString(),
        open: quote.price,
        high: quote.price,
        low: quote.price,
        close: quote.price,
        volume: 0,
      });
      return;
    }
    existing.high = Math.max(existing.high, quote.price);
    existing.low = Math.min(existing.low, quote.price);
    existing.close = quote.price;
  });

  const mergedByTimestamp = new Map(baseCandles.map((candle) => [candle.timestamp, candle]));
  let tailChanged = false;
  Array.from(buckets.values())
    .filter((bucket) => {
      const bucketMsValue = normalizeQuoteTimestamp(bucket.timestamp);
      return Number.isFinite(bucketMsValue) && bucketMsValue >= lastBaseMs;
    })
    .forEach((bucket) => {
      const existing = mergedByTimestamp.get(bucket.timestamp);
      if (
        !existing
        || Number(existing.open) !== Number(bucket.open)
        || Number(existing.high) !== Number(bucket.high)
        || Number(existing.low) !== Number(bucket.low)
        || Number(existing.close) !== Number(bucket.close)
      ) {
        tailChanged = true;
      }
      mergedByTimestamp.set(bucket.timestamp, bucket);
    });

  const mergedCandles = Array.from(mergedByTimestamp.values()).sort(
    (left, right) => normalizeQuoteTimestamp(left.timestamp) - normalizeQuoteTimestamp(right.timestamp),
  );

  return {
    candles: mergedCandles,
    source: baseSource.startsWith("historical_store")
      ? "historical_store_with_live_tail"
      : `${baseSource}_with_live_tail`,
    tailApplied: tailChanged,
    firstTimestamp: mergedCandles[0]?.timestamp || null,
    lastTimestamp: mergedCandles[mergedCandles.length - 1]?.timestamp || null,
    count: mergedCandles.length,
  };
};

const marketDataSourcePriority = (source) => {
  switch (source) {
    case "historical_store_with_live_tail":
      return 6;
    case "historical_store":
    case "topstepx_with_store_backfill":
      return 5;
    case "topstepx":
      return 4;
    case "historical_store_partial":
      return 3;
    case "topstepx_partial":
      return 2;
    case "simulated":
      return 1;
    case "error":
    case "unknown":
    default:
      return 0;
  }
};

const formatPriceValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCurrencyValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatSignedCurrencyValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `${numeric >= 0 ? "+" : "-"}${Math.abs(numeric).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatChartBadgePnlValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "$0.00";
  const absoluteValue = Math.abs(numeric).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return numeric < 0 ? `-${absoluteValue}` : absoluteValue;
};

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const firstFiniteNumber = (...values) => {
  for (const value of values) {
    const numeric = toFiniteNumber(value);
    if (numeric !== null) {
      return numeric;
    }
  }
  return null;
};

const extractRealtimeAccountId = (payload) => {
  const candidates = [
    payload?.accountId,
    payload?.account_id,
    payload?.accountID,
    payload?.externalAccountId,
    payload?.external_account_id,
    payload?.account?.id,
    payload?.account?.accountId,
    payload?.accountInfo?.id,
    payload?.accountInfo?.accountId,
    payload?.data?.accountId,
    payload?.data?.account_id,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    return String(candidate);
  }

  return "";
};

const extractRealtimeDailyPnl = (payload) => {
  const explicitDaily = firstFiniteNumber(
    payload?.dailyPnl,
    payload?.daily_pnl,
    payload?.dayPnl,
    payload?.day_pnl,
  );
  if (explicitDaily !== null) {
    return explicitDaily;
  }

  const realized = firstFiniteNumber(payload?.realizedPnl, payload?.realized_pnl);
  const unrealized = firstFiniteNumber(
    payload?.unrealizedPnl,
    payload?.unrealized_pnl,
    payload?.openPnl,
    payload?.open_pnl,
  );
  if (realized !== null && unrealized !== null) {
    return realized + unrealized;
  }

  const fallbackAggregate = firstFiniteNumber(payload?.pnl);
  if (
    fallbackAggregate !== null
    && realized === null
    && unrealized === null
  ) {
    return fallbackAggregate;
  }

  return null;
};

const strategyRefFromLink = (link) => {
  if (!link) return "none";
  if ((link.strategy_kind || "visual") === "runtime") {
    const inferredEngine = inferStrategyEngine(
      link?.strategy_name || link?.strategy_name_snapshot || "",
      link?.runtime_engine || null,
    );
    if (STRATEGY_ENGINES.some((engine) => engine.value === inferredEngine)) {
      return inferredEngine;
    }
    return "none";
  }
  if ((link.strategy_kind || "visual") === "catalog" && link.catalog_strategy_id) {
    return `catalog:${link.catalog_strategy_id}`;
  }
  if ((link.strategy_kind || "visual") === "custom" && link.user_script_id) {
    return `custom:${link.user_script_id}`;
  }
  return link.strategy_id || "none";
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCompactLayout = useMediaQuery("(max-width: 1023px)");
  const appTimeZone = resolveDisplayTimezone(user?.timezone);
  const chartTimeZone = appTimeZone || BROWSER_TIMEZONE;

  useEffect(() => {
    if (!user) return;
    
    const token = localStorage.getItem('token');
    const eventSource = new EventSource(buildApiUrl(`/api/user/pnl-stream?token=${token}`));
    
    eventSource.addEventListener('pnl_update', (event) => {
      try {
        const data = JSON.parse(event.data);
        setRealtimeUnrealizedPnl(data.total_unrealized_pnl || 0);
        
        const pnlMap = {};
        (data.positions || []).forEach(p => {
          const streamPnl = firstFiniteNumber(p?.unrealized_pnl);
          const streamIsLive = Boolean(p?.pnl_live);
          if (p?.trade_id && streamPnl !== null && (streamIsLive || Math.abs(streamPnl) > 0.005)) {
            pnlMap[p.trade_id] = streamPnl;
          }
        });
        setRealtimePositionPnls(pnlMap);
      } catch (err) {
        console.error("Error parsing P&L stream data:", err);
      }
    });
    
    eventSource.onerror = (err) => {
      console.error("P&L stream error:", err);
      // eventSource.close();
    };
    
    return () => {
      eventSource.close();
    };
  }, [user]);

  const [symbol, setSymbol] = useState(() => localStorage.getItem("tp_chart_symbol") || "");
  const [timeframe, setTimeframe] = useState(() => {
    const saved = localStorage.getItem("tp_chart_timeframe");
    if (saved === "2mn") return "2m"; // migrate old label
    return saved || "5m";
  });
  const [chartPrefs, setChartPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("tp_chart_prefs") || "{}");
    } catch {
      return {};
    }
  });
  const [candles, setCandles] = useState([]);
  const [indicators, setIndicators] = useState({});
  const [signals, setSignals] = useState([]);
  const [account, setAccount] = useState(null);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [brokerCatalogOptions, setBrokerCatalogOptions] = useState([]);
  const [selectedTradingAccountId, setSelectedTradingAccountId] = useState(
    () => localStorage.getItem("tp_dashboard_account_id") || "",
  );
  const [positions, setPositions] = useState([]);
  const [brokerState, setBrokerState] = useState({ state: "ok", lastConfirmedAt: null, warning: null });
  const [realtimeUnrealizedPnl, setRealtimeUnrealizedPnl] = useState(0);
  const [realtimePositionPnls, setRealtimePositionPnls] = useState({});
  const [timeframeLadder, setTimeframeLadder] = useState({
    timeframes: [],
    priorDay: {},
    available: true,
    loading: false,
  });
  const [decisionTimeline, setDecisionTimeline] = useState({
    decisions: [],
    counts: {},
    windowHours: 24,
    loading: false,
    error: null,
  });
  const [trades, setTrades] = useState([]);
  const [orderDiagnostics, setOrderDiagnostics] = useState(null);
  const [orderDiagnosticsByScope, setOrderDiagnosticsByScope] = useState({});
  const [orderDiagnosticsLoading, setOrderDiagnosticsLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [newsEvents, setNewsEvents] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  
  const [activeTab, setActiveTab] = useState("chart");
  const [mobileSection, setMobileSection] = useState("chart");
  const [mobileInsightTab, setMobileInsightTab] = useState("signals");
  const [mobileChartControlsOpen, setMobileChartControlsOpen] = useState(false);
  const [mobileSymbolsExpanded, setMobileSymbolsExpanded] = useState(false);
  const [visualStrategies, setVisualStrategies] = useState([]);
  const [catalogStrategies, setCatalogStrategies] = useState([]);
  const [customStrategies, setCustomStrategies] = useState([]);
  const [accountStrategyLinks, setAccountStrategyLinks] = useState([]);
  const [savingAutoTradeContracts, setSavingAutoTradeContracts] = useState(false);
  const [selectedStrategyRef, setSelectedStrategyRef] = useState("none");
  const [strategyInputsOpen, setStrategyInputsOpen] = useState(false);
  const [chartStrategyDraft, setChartStrategyDraft] = useState(
    normalizeRuntimeDefinition(null, "TREND_FOLLOWING", "runtime", "dashboard"),
  );
  const [appliedChartStrategy, setAppliedChartStrategy] = useState(null);
  const [strategyChartData, setStrategyChartData] = useState({
    overlays: [],
    markers: [],
    summary: null,
    signals: [],
    indicatorSnapshot: null,
    paritySignature: null,
    dataQuality: null,
  });
  const [marketDataMeta, setMarketDataMeta] = useState({
    scopeKey: getMarketDataScopeKey(symbol, TIMEFRAMES[timeframe] || 5),
    source: "unknown",
    count: 0,
    firstTimestamp: null,
    lastTimestamp: null,
    dataQuality: null,
  });
  const [dataFetchPulse, setDataFetchPulse] = useState(false);
  const dataFetchPulseTimerRef = useRef(null);

  // Helper to update position marks efficiently
  const mergePositionMark = useCallback((symbol, markPrice) => {
    setPositionMarksBySymbol((prev) => ({
      ...prev,
      [symbol]: markPrice,
    }));
  }, []);

  // Auto-trading state
  const [adoptExternalTrades, setAdoptExternalTrades] = useState(false);
  const [autoTradeEnabled, setAutoTradeEnabled] = useState(false);
  const [paperMode, setPaperMode] = useState(true);
  const [aiGuidedTradingEnabled, setAiGuidedTradingEnabled] = useState(false);
  const [aiGuidedTradingAvailable, setAiGuidedTradingAvailable] = useState(false);
  const [aiResearchInstruments, setAiResearchInstruments] = useState(["MES", "MNQ", "MCL", "MGC", "SIL", "M2K"]);
  const [aiResearchAllowLimitOrders, setAiResearchAllowLimitOrders] = useState(true);
  const [autoTradeAccountIds, setAutoTradeAccountIds] = useState([]);
  const [scopeAutoTradeEnabled, setScopeAutoTradeEnabled] = useState(false);
  const [scopePaperMode, setScopePaperMode] = useState(false);
  const [latestAutomationRun, setLatestAutomationRun] = useState(null);
  const [latestAutomationRunLoading, setLatestAutomationRunLoading] = useState(false);
  const [signalAlerts, setSignalAlerts] = useState([]);
  const [signalAnalysis, setSignalAnalysis] = useState(null);
  const [scanningSignals, setScanningSignals] = useState(false);
  const [refreshRateMs, setRefreshRateMs] = useState(5000);

  // Fetch global config on mount and periodically (so admin changes take effect)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await getPublicConfig();
        if (res.data && Object.prototype.hasOwnProperty.call(res.data, "data_refresh_rate_ms")) {
          setRefreshRateMs(getDashboardPollIntervalMs(res.data.data_refresh_rate_ms));
        }
      } catch (error) {
        console.error("Error fetching system config:", error);
      }
    };
    fetchConfig();
    const configPollInterval = setInterval(fetchConfig, 30000);
    return () => clearInterval(configPollInterval);
  }, []);

  // Market data / WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [bidPrice, setBidPrice] = useState(0);
  const [askPrice, setAskPrice] = useState(0);
  const [dayHigh, setDayHigh] = useState(0);
  const [dayLow, setDayLow] = useState(0);
  const [volume, setVolume] = useState(0);
  const [dayOpenPrice, setDayOpenPrice] = useState(0);
  const [priceChange, setPriceChange] = useState(0);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [positionMarksBySymbol, setPositionMarksBySymbol] = useState({});
  const [contractMap, setContractMap] = useState({});
  const [loading, setLoading] = useState(false);

  const wsRef = useRef(null);
  const selectedSymbolRef = useRef(symbol);
  const currentContractRef = useRef(null);
  const quoteUnsubscribeRef = useRef(null);
  const depthUnsubscribeRef = useRef(null);
  const signalScanInterval = useRef(null);
  const latestMarketRequestRef = useRef(0);
  const marketFetchInFlightRef = useRef(false);
  const latestAccountSnapshotRequestRef = useRef(0);
  const snapshotFetchInFlightRef = useRef(false);
  const adoptTradesUpdatingRef = useRef(false);
  const lastQuoteReceivedAtRef = useRef(0);
  const liveQuoteActiveRef = useRef(false);
  const lastDisplayedPriceRef = useRef(0);
  const strategySyncCursorRef = useRef({
    scopeKey: "",
    linkedRef: "",
  });
  const accountEventRefreshTimeoutRef = useRef(null);
  const orderDiagnosticsRefreshTimeoutRef = useRef(null);
  const orderDiagnosticsRequestRef = useRef(0);
  const latestAutomationRunRequestRef = useRef(0);
  const latestAutomationRunScopeRef = useRef("");
  const latestAutomationRunSignatureRef = useRef("__uninitialized__");
  const autoTradeIntervalSyncRef = useRef("");
  const symbolSelectionStateRef = useRef({
    scopeKey: "",
    manual: false,
    positionResolved: false,
  });
  const positionsRef = useRef([]);
  const initialLoadStartedRef = useRef(false);
  // Tracks whether the auto-trade-scope-aware default selection has been applied.
  // Set true on first successful application so subsequent renders / manual changes are not overwritten.
  const scopeDefaultsAppliedRef = useRef(false);
  const marketDataSnapshotRef = useRef({
    scopeKey: getMarketDataScopeKey(symbol, TIMEFRAMES[timeframe] || 5),
    source: "unknown",
    candleCount: 0,
  });

  const displayedPaperMode = selectedTradingAccountId ? scopePaperMode : paperMode;
  const selectedIntervalMinutes = TIMEFRAMES[timeframe] || 5;

  const accountContractLinks = useMemo(
    () => accountStrategyLinks.filter((link) => (
      link.trading_account_id === selectedTradingAccountId
      && linkMatchesSelectedSymbol(link, symbol)
    )),
    [accountStrategyLinks, selectedTradingAccountId, symbol],
  );

  const activeAccountContractLink = useMemo(
    () => accountContractLinks.find((link) => link.is_active) || null,
    [accountContractLinks],
  );

  const linkedStrategyRef = useMemo(
    () => strategyRefFromLink(activeAccountContractLink),
    [activeAccountContractLink],
  );

  const selectedScopeHasActiveLink = Boolean(activeAccountContractLink?.is_active);

  const autoTradeEnabledForSelectedAccount = selectedTradingAccountId
    ? scopeAutoTradeEnabled
    : autoTradeEnabled;

  const paperModeForSelectedScope = selectedTradingAccountId
    ? (scopeAutoTradeEnabled ? scopePaperMode : false)
    : (autoTradeEnabled ? paperMode : false);

  const selectedAccountIsInAutoTradeScope = selectedTradingAccountId
    ? autoTradeAccountIds.includes(selectedTradingAccountId)
    : false;

  const selectedTradingAccount = useMemo(
    () => tradingAccounts.find((entry) => entry.id === selectedTradingAccountId) || null,
    [selectedTradingAccountId, tradingAccounts],
  );

  const shouldPreferBrokerMarketData = Boolean(
    selectedTradingAccountId
    && (selectedTradingAccount?.is_connected || account?.is_connected)
  );

  const selectedExternalAccountId = useMemo(
    () => String(selectedTradingAccount?.external_account_id || account?.external_account_id || "").trim(),
    [account?.external_account_id, selectedTradingAccount?.external_account_id],
  );

  const currentOrderDiagnostics = useMemo(() => {
    const effectiveAccountId = selectedTradingAccountId || account?.account_id || null;
    const scopeKey = getOrderDiagnosticsScopeKey(effectiveAccountId, symbol);
    const normalizedSelectedSymbol = normalizeInstrumentSymbol(symbol);

    if (
      orderDiagnostics
      && normalizeInstrumentSymbol(orderDiagnostics?.symbol) === normalizedSelectedSymbol
      && (
        !effectiveAccountId
        || !orderDiagnostics?.trading_account_id
        || String(orderDiagnostics.trading_account_id) === String(effectiveAccountId)
      )
    ) {
      return orderDiagnostics;
    }

    return orderDiagnosticsByScope[scopeKey] || null;
  }, [
    account?.account_id,
    orderDiagnostics,
    orderDiagnosticsByScope,
    selectedTradingAccountId,
    symbol,
  ]);

  const effectiveAccountBalance = Number(account?.balance ?? selectedTradingAccount?.balance ?? 0);
  const effectiveDailyPnl = Number(account?.daily_pnl ?? selectedTradingAccount?.daily_pnl ?? 0);
  const effectiveBuyingPower = Number(account?.buying_power ?? selectedTradingAccount?.buying_power ?? 0);

  const availableInstrumentOptions = useMemo(() => {
    if (selectedTradingAccount?.supported_instruments?.length) {
      const unionSymbols = Array.from(new Set([
        ...selectedTradingAccount.supported_instruments,
        ...TRADOPRO_CORE_SYMBOLS,
      ]));
      return instrumentOptionsFromSymbols(unionSymbols);
    }

    return brokerCatalogOptions;
  }, [brokerCatalogOptions, selectedTradingAccount, selectedTradingAccountId, tradingAccounts]);

  const selectedInstrumentOption = useMemo(
    () => availableInstrumentOptions.find((option) => option.value === symbol) || null,
    [availableInstrumentOptions, symbol],
  );

  const accountSelectValue = selectedTradingAccountId || (tradingAccounts.length ? tradingAccounts[0].id : "none");

  const activeChartStrategyRef = appliedChartStrategy?.ref && appliedChartStrategy.ref !== "none"
    ? appliedChartStrategy.ref
    : "none";

  const strategyOptions = useMemo(() => {
    return buildStrategyOptions({
      visualStrategies,
      catalogStrategies,
      customStrategies,
      includeBuiltIns: true,
      includeNone: true,
    });
  }, [visualStrategies, catalogStrategies, customStrategies]);

  const selectedStrategyOption = useMemo(
    () => strategyOptions.find((option) => option.value === selectedStrategyRef) || strategyOptions[0] || null,
    [strategyOptions, selectedStrategyRef],
  );

  const showVolume = chartPrefs.showVolume ?? false;
  const showSMA = chartPrefs.showSMA ?? true;
  const showEMA = chartPrefs.showEMA ?? true;

  const displayedCandlePayload = useMemo(() => appendQuoteTailToCandles({
    candles,
    recentQuotes,
    timeframe,
    source: marketDataMeta.source,
    wsConnected,
  }), [candles, marketDataMeta.source, recentQuotes, timeframe, wsConnected]);

  const chartCandles = useMemo(
    () => (displayedCandlePayload.tailApplied ? displayedCandlePayload.candles : candles),
    [candles, displayedCandlePayload.candles, displayedCandlePayload.tailApplied],
  );
  const latestChartClose = useMemo(() => {
    const latestChartCandle = chartCandles[chartCandles.length - 1];
    const latestBaseCandle = candles[candles.length - 1];
    return firstFiniteNumber(latestChartCandle?.close, latestBaseCandle?.close);
  }, [candles, chartCandles]);

  const chartRenderKey = useMemo(
    () => `${symbol}:${timeframe}`,
    [symbol, timeframe],
  );
  const chartDataQuality = marketDataMeta.dataQuality || strategyChartData.dataQuality || null;
  const strategyParitySignatureShort = shortenParitySignature(strategyChartData.paritySignature);

  const hasActiveSelectedSymbolPosition = useMemo(() => {
    const normalizedSelectedSymbol = normalizeInstrumentSymbol(symbol);
    if (!normalizedSelectedSymbol) {
      return false;
    }
    return positions.some(
      (position) => normalizeInstrumentSymbol(position?.symbol) === normalizedSelectedSymbol,
    );
  }, [positions, symbol]);
  const selectedSymbolPositionTruth = useMemo(() => {
    const normalizedSelectedSymbol = normalizeInstrumentSymbol(symbol);
    const scopedPositions = positions.filter(
      (position) => normalizeInstrumentSymbol(position?.symbol) === normalizedSelectedSymbol,
    );
    const normalizedStatuses = scopedPositions.map((position) => ({
      protection: String(position?.protection_status || "").trim().toLowerCase(),
      stop: String(position?.protective_stop_status || "").trim().toLowerCase(),
      unsafe: Boolean(position?.account_symbol_unsafe || position?.protection_warning),
      hasConfirmedStopEvidence: Boolean(
        (position?.broker_stop_order_id || position?.protective_stop_order_id)
        && Number.isFinite(Number(position?.broker_confirmed_stop_price)),
      ),
    }));
    const unsafePosition = normalizedStatuses.some((status) => (
      status.unsafe
      || status.protection.includes("failed")
      || status.protection === "unprotected"
      || status.stop.includes("failed")
    ));
    const protectionConfirmed = normalizedStatuses.length > 0 && normalizedStatuses.every((status) => (
      status.protection === "broker_stop_confirmed"
      || status.protection === "paper"
      || status.stop === "confirmed"
      || status.hasConfirmedStopEvidence
    ));
    const protectionPending = !unsafePosition && normalizedStatuses.some((status) => (
      status.protection.includes("pending")
      || status.stop.includes("pending")
      || status.stop.includes("queued")
    ));
    return {
      hasOpenPosition: scopedPositions.length > 0,
      unsafePosition,
      protectionConfirmed,
      protectionPending,
    };
  }, [positions, symbol]);
  const positionEntryReferenceBySymbol = useMemo(() => {
    const aggregates = {};
    positions.forEach((position) => {
      const normalizedSymbol = normalizeInstrumentSymbol(position?.symbol);
      const quantityValue = firstFiniteNumber(position?.quantity);
      const entryPrice = firstFiniteNumber(position?.entry_price);
      if (!normalizedSymbol || quantityValue === null || entryPrice === null || quantityValue <= 0 || entryPrice <= 0) {
        return;
      }
      const current = aggregates[normalizedSymbol] || { quantity: 0, weightedEntry: 0 };
      current.quantity += quantityValue;
      current.weightedEntry += quantityValue * entryPrice;
      aggregates[normalizedSymbol] = current;
    });

    return Object.fromEntries(
      Object.entries(aggregates)
        .map(([normalizedSymbol, aggregate]) => {
          const averageEntry = aggregate.quantity > 0 ? aggregate.weightedEntry / aggregate.quantity : 0;
          return [normalizedSymbol, averageEntry > 0 ? averageEntry : null];
        }),
    );
  }, [positions]);

  // Format chart data - use real-time quotes when available
  const chartData = useMemo(() => (
    wsConnected && recentQuotes.length > 0
      ? recentQuotes.map((quote, index) => ({
          time: quote.time,
          close: quote.price,
          bid: quote.bid,
          ask: quote.ask,
          index
        }))
      : candles.map((candle, index) => ({
          time: formatTimeDisplay(candle.timestamp, chartTimeZone, { hour12: false }),
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          volume: candle.volume,
          index
        }))
  ), [candles, chartTimeZone, recentQuotes, wsConnected]);

  const chartIndicators = useMemo(() => {
    const values = candles || [];
    if (!values.length) return {};

    const toPoint = (idx, value) => {
      const c = values[idx];
      const ts = c?.timestamp;
      const time = typeof ts === "number"
        ? (ts > 1e12 ? Math.floor(ts / 1000) : Math.floor(ts))
        : Math.floor((parseDateValue(ts)?.getTime() ?? NaN) / 1000);
      return Number.isFinite(time) ? { time, value: Number(value) } : null;
    };

    const smaWindow = 20;
    const emaWindow = 21;

    const sma = [];
    const ema = [];

    let emaPrev = null;
    const k = 2 / (emaWindow + 1);

    for (let i = 0; i < values.length; i++) {
      const close = Number(values[i].close);
      if (!Number.isFinite(close)) continue;

      if (i >= smaWindow - 1) {
        const slice = values.slice(i - smaWindow + 1, i + 1).map(v => Number(v.close));
        const valid = slice.every(Number.isFinite);
        if (valid) {
          const avg = slice.reduce((a, b) => a + b, 0) / smaWindow;
          const p = toPoint(i, avg);
          if (p) sma.push(p);
        }
      }

      emaPrev = emaPrev == null ? close : (close * k + emaPrev * (1 - k));
      const ep = toPoint(i, emaPrev);
      if (ep) ema.push(ep);
    }

    return {
      ...(showSMA ? { sma } : {}),
      ...(showEMA ? { ema } : {}),
    };
  }, [candles, showSMA, showEMA]);

  // Keep the account snapshot bias aligned with the chart rather than a noisy
  // intraday price-change heuristic.
  const trend = useMemo(() => {
    const stDir = firstFiniteNumber(indicators.supertrend_direction, 0) ?? 0;
    const ema9 = firstFiniteNumber(indicators.ema_9, 0) ?? 0;
    const ema21 = firstFiniteNumber(indicators.ema_21, 0) ?? 0;
    const referencePrice = firstFiniteNumber(latestChartClose, currentPrice, 0) ?? 0;

    let score = 0;

    if (stDir > 0) score += 2;
    else if (stDir < 0) score -= 2;

    if (ema9 > 0 && ema21 > 0) {
      if (ema9 > ema21) score += 1;
      else if (ema9 < ema21) score -= 1;
    }

    if (referencePrice > 0) {
      if (ema9 > 0) {
        if (referencePrice > ema9) score += 1;
        else if (referencePrice < ema9) score -= 1;
      }
      if (ema21 > 0) {
        if (referencePrice > ema21) score += 1;
        else if (referencePrice < ema21) score -= 1;
      }
    }

    if (score >= 2) return "BULLISH";
    if (score <= -2) return "BEARISH";
    return score > 0 ? "NEUTRAL-BULL" : score < 0 ? "NEUTRAL-BEAR" : "NEUTRAL";
  }, [currentPrice, indicators.ema_9, indicators.ema_21, indicators.supertrend_direction, latestChartClose]);
  const displayedSignals = useMemo(() => {
    const raw = appliedChartStrategy ? (strategyChartData.signals || []) : signals;
    return (raw || []).slice(-100).reverse();
  }, [appliedChartStrategy, signals, strategyChartData.signals]);

  const mergeAccountSnapshotIntoAccounts = useCallback((snapshot) => {
    if (!snapshot?.account_id) {
      return;
    }

    setTradingAccounts((current) => current.map((entry) => {
      if (entry.id !== snapshot.account_id) {
        return entry;
      }
      return {
        ...entry,
        account_name: snapshot.account_name ?? entry.account_name,
        account_type: snapshot.account_type ?? entry.account_type,
        balance: snapshot.balance ?? entry.balance,
        buying_power: snapshot.buying_power ?? entry.buying_power,
        daily_pnl: snapshot.daily_pnl ?? entry.daily_pnl,
        supported_instruments: Array.isArray(snapshot.supported_instruments)
          ? snapshot.supported_instruments
          : entry.supported_instruments,
        broker_display_name: snapshot.broker_display_name ?? entry.broker_display_name,
        external_account_id: snapshot.external_account_id ?? entry.external_account_id,
        topstep_bracket_mode: snapshot.topstep_bracket_mode ?? entry.topstep_bracket_mode,
      };
    }));
  }, []);

  const fetchPositionMarks = useCallback(async (targetPositions) => {
    const sourcePositions = Array.isArray(targetPositions) ? targetPositions : positionsRef.current;
    const symbolsToRefresh = Array.from(
      new Set(
        (sourcePositions || [])
          .map((position) => normalizeInstrumentSymbol(position?.symbol))
          .filter(Boolean),
      ),
    );

    if (!symbolsToRefresh.length) {
      return;
    }

    const symbolReferencePrices = {};
    sourcePositions.forEach((position) => {
      const normalizedSymbol = normalizeInstrumentSymbol(position?.symbol);
      const quantityValue = firstFiniteNumber(position?.quantity);
      const entryPrice = firstFiniteNumber(position?.entry_price);
      if (!normalizedSymbol || quantityValue === null || entryPrice === null || quantityValue <= 0 || entryPrice <= 0) {
        return;
      }
      const current = symbolReferencePrices[normalizedSymbol] || { quantity: 0, weightedEntry: 0 };
      current.quantity += quantityValue;
      current.weightedEntry += quantityValue * entryPrice;
      symbolReferencePrices[normalizedSymbol] = current;
    });

    try {
      const responses = await Promise.all(
        symbolsToRefresh.map(async (positionSymbol) => {
          const res = await getQuote({ symbol: positionSymbol });
          return [positionSymbol, Number(res.data?.current_price || res.data?.quote?.price || 0)];
        }),
      );

      setPositionMarksBySymbol((current) => {
        let changed = false;
        const next = { ...current };

        responses.forEach(([positionSymbol, closePrice]) => {
          if (!Number.isFinite(closePrice) || closePrice <= 0) {
            return;
          }
          const aggregateReference = symbolReferencePrices[positionSymbol];
          const referencePrice = firstFiniteNumber(
            aggregateReference?.quantity > 0 ? (aggregateReference.weightedEntry / aggregateReference.quantity) : null,
            current[positionSymbol],
          );
          if (!isCompatibleLivePrice(closePrice, referencePrice)) {
            return;
          }
          if (next[positionSymbol] !== closePrice) {
            next[positionSymbol] = closePrice;
            changed = true;
          }
        });

        return changed ? next : current;
      });
    } catch (error) {
      console.debug("Error refreshing position marks:", error);
    }
  }, []);

  const fetchAutoTradeEffectiveState = useCallback(async (accountId = selectedTradingAccountId, selectedSymbol = symbol) => {
    if (!accountId) {
      setScopeAutoTradeEnabled(autoTradeEnabled);
      setScopePaperMode(paperMode);
      return;
    }
    try {
      const res = await getAutoTradeEffectiveState({
          trading_account_id: accountId,
          symbol: selectedSymbol,
        });
      setScopeAutoTradeEnabled(Boolean(res.data?.effective_auto_trade_enabled));
      setScopePaperMode(Boolean(res.data?.effective_paper_mode));
    } catch (error) {
      console.error("Error fetching auto-trade effective state:", error);
      setScopeAutoTradeEnabled(false);
      setScopePaperMode(false);
    }
  }, [autoTradeEnabled, paperMode, selectedTradingAccountId, symbol]);

  const getSignalAnalysis = useCallback(async () => {
    try {
      const res = await getSignalsAnalysis(symbol);
      setSignalAnalysis(res.data);
    } catch (error) {
      console.error("Error getting signal analysis:", error);
    }
  }, [symbol]);

  const fetchAccountSnapshot = useCallback(async (accountId = selectedTradingAccountId) => {
    if (!accountId) {
      return;
    }
    // Skip if a previous snapshot fetch is still in flight
    if (snapshotFetchInFlightRef.current) return;
    snapshotFetchInFlightRef.current = true;

    const requestId = latestAccountSnapshotRequestRef.current + 1;
    latestAccountSnapshotRequestRef.current = requestId;

    try {
      const [accountRes, positionsRes] = await Promise.all([
        getAccount({ trading_account_id: accountId }),
        getPositions({ trading_account_id: accountId }),
      ]);
      if (latestAccountSnapshotRequestRef.current !== requestId) {
        return;
      }
      setAccount(accountRes.data);
      if (!adoptTradesUpdatingRef.current) setAdoptExternalTrades(Boolean(accountRes.data?.adopt_external_trades));
      mergeAccountSnapshotIntoAccounts(accountRes.data);
      setPositions(positionsRes.data?.positions || []);
      setBrokerState({
        state: positionsRes.data?.broker_state || "ok",
        lastConfirmedAt: positionsRes.data?.broker_last_confirmed_at || null,
        warning: positionsRes.data?.broker_sync_warning || null,
      });
      fetchPositionMarks(positionsRes.data?.positions || []);
    } catch (error) {
      console.error("Error fetching account snapshot:", error);
    } finally {
      snapshotFetchInFlightRef.current = false;
    }
  }, [displayedPaperMode, fetchPositionMarks, mergeAccountSnapshotIntoAccounts, selectedTradingAccountId]);

  const fetchAccountData = useCallback(async () => {
    try {
      const accountsRes = await getAccounts();
      const availableAccounts = accountsRes.data?.accounts || [];
      setTradingAccounts(availableAccounts);

      let effectiveAccountId = selectedTradingAccountId;
      if (!availableAccounts.length) {
        effectiveAccountId = "";
      } else if (!effectiveAccountId || !availableAccounts.some((entry) => entry.id === effectiveAccountId)) {
        const activePositionsRes = await getPositions().catch(() => ({ data: { positions: [] } }));
        const preferredAccountId = (activePositionsRes.data?.positions || [])
          .map((position) => position?.trading_account_id)
          .find((accountId) => availableAccounts.some((entry) => entry.id === accountId));
        effectiveAccountId = preferredAccountId || availableAccounts[0].id;
      }

      if (effectiveAccountId !== selectedTradingAccountId) {
        setSelectedTradingAccountId(effectiveAccountId);
      }

      const accountQueryParams = effectiveAccountId ? { trading_account_id: effectiveAccountId } : undefined;
      const [accountRes, positionsRes, tradesRes] = await Promise.all([
        getAccount(accountQueryParams),
        getPositions(accountQueryParams),
        getTrades(effectiveAccountId ? { limit: 20, trading_account_id: effectiveAccountId } : { limit: 20 }),
      ]);

      setAccount(accountRes.data);
      if (!adoptTradesUpdatingRef.current) setAdoptExternalTrades(Boolean(accountRes.data?.adopt_external_trades));
      mergeAccountSnapshotIntoAccounts(accountRes.data);
      setPositions(positionsRes.data.positions);
      setBrokerState({
        state: positionsRes.data?.broker_state || "ok",
        lastConfirmedAt: positionsRes.data?.broker_last_confirmed_at || null,
        warning: positionsRes.data?.broker_sync_warning || null,
      });
      setTrades(tradesRes.data.trades);
      fetchPositionMarks(positionsRes.data.positions);
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  }, [fetchPositionMarks, mergeAccountSnapshotIntoAccounts, paperMode, scopePaperMode, selectedTradingAccountId]);

  const fetchAutoTradeStatus = useCallback(async () => {
    try {
      const res = await getAutoTradeStatus(
        selectedTradingAccountId ? { trading_account_id: selectedTradingAccountId } : undefined
      );
      setAutoTradeEnabled(res.data.enabled);
      setPaperMode(res.data.paper_mode);
      setAiGuidedTradingEnabled(
        selectedTradingAccountId ? Boolean(res.data.ai_guided_trading_enabled) : false
      );
      setAiGuidedTradingAvailable(Boolean(res.data.ai_guided_trading_available));
      setAiResearchInstruments(Array.isArray(res.data.ai_research_instruments) ? res.data.ai_research_instruments : ["MES", "MNQ", "MCL", "MGC", "SIL", "M2K"]);
      setAiResearchAllowLimitOrders(Boolean(res.data.ai_research_allow_limit_orders));
      setAutoTradeAccountIds(Array.isArray(res.data.enabled_account_ids) ? res.data.enabled_account_ids : []);
      setScanningSignals(Boolean(res.data.signal_scan_enabled));
      if (selectedTradingAccountId && symbol) {
        await fetchAutoTradeEffectiveState(selectedTradingAccountId, symbol);
      } else {
        setScopeAutoTradeEnabled(Boolean(res.data.enabled));
        setScopePaperMode(Boolean(res.data.paper_mode));
      }
      if (res.data.alert_strategy_ref && selectedStrategyRef === "none") {
        setSelectedStrategyRef(res.data.alert_strategy_ref);
      }
    } catch (error) {
      console.error("Error fetching auto-trade status:", error);
    }
  }, [fetchAutoTradeEffectiveState, selectedStrategyRef, selectedTradingAccountId, symbol]);

  const toggleAiGuidedTrading = useCallback(async (checked) => {
    if (!selectedTradingAccountId) {
      toast.error("Select a trading account before changing AI Research Trader");
      return;
    }
    const nextEnabled = Boolean(checked);
    try {
      const res = await setAiGuidedTrading({
        enabled: nextEnabled,
        trading_account_id: selectedTradingAccountId,
      });
      setAiGuidedTradingEnabled(Boolean(res.data.ai_guided_trading_enabled));
      setAiGuidedTradingAvailable(Boolean(res.data.ai_guided_trading_available));
      setAiResearchInstruments(Array.isArray(res.data.ai_research_instruments) ? res.data.ai_research_instruments : aiResearchInstruments);
      setAiResearchAllowLimitOrders(Boolean(res.data.ai_research_allow_limit_orders));
      toast.success(`AI Research Trader ${nextEnabled ? "enabled" : "disabled"} for this account`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update AI-guided trading"));
    }
  }, [aiResearchInstruments, selectedTradingAccountId]);

  const updateAiResearchSettings = useCallback(async (updates) => {
    if (!selectedTradingAccountId) {
      toast.error("Select a trading account before changing AI Research Trader settings");
      return;
    }
    try {
      const res = await setAiGuidedTrading({
        enabled: aiGuidedTradingEnabled,
        trading_account_id: selectedTradingAccountId,
        ...updates,
      });
      setAiResearchInstruments(Array.isArray(res.data.ai_research_instruments) ? res.data.ai_research_instruments : aiResearchInstruments);
      setAiResearchAllowLimitOrders(Boolean(res.data.ai_research_allow_limit_orders));
      toast.success("AI Research Trader settings saved");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update AI Research Trader settings"));
    }
  }, [aiGuidedTradingEnabled, aiResearchInstruments, selectedTradingAccountId]);

  const fetchLatestAutomationRun = useCallback(async (accountId = selectedTradingAccountId, selectedSymbol = symbol) => {
    if (!accountId || !selectedSymbol) {
      latestAutomationRunScopeRef.current = "";
      latestAutomationRunSignatureRef.current = "__uninitialized__";
      setLatestAutomationRun((current) => (current === null ? current : null));
      setLatestAutomationRunLoading((current) => (current ? false : current));
      return;
    }

    const scopeKey = `${accountId}::${selectedSymbol}`;
    const scopeChanged = latestAutomationRunScopeRef.current !== scopeKey;
    if (scopeChanged) {
      latestAutomationRunScopeRef.current = scopeKey;
      latestAutomationRunSignatureRef.current = "__uninitialized__";
      setLatestAutomationRun(null);
      setLatestAutomationRunLoading(true);
    } else if (latestAutomationRunSignatureRef.current === "__uninitialized__") {
      setLatestAutomationRunLoading(true);
    }

    const requestId = latestAutomationRunRequestRef.current + 1;
    latestAutomationRunRequestRef.current = requestId;

    try {
      const res = await getAutoTradeRuns({
        limit: 1,
        trading_account_id: accountId,
        symbol: selectedSymbol,
      });
      if (latestAutomationRunRequestRef.current !== requestId) {
        return;
      }
      const nextRun = (res.data?.runs || [])[0] || null;
      const nextSignature = buildAutomationRunSignature(nextRun);
      if (latestAutomationRunSignatureRef.current !== nextSignature) {
        latestAutomationRunSignatureRef.current = nextSignature;
        setLatestAutomationRun(nextRun);
      }
    } catch (error) {
      if (latestAutomationRunRequestRef.current !== requestId) {
        return;
      }
      console.error("Error fetching latest automation run:", error);
    } finally {
      if (latestAutomationRunRequestRef.current === requestId) {
        setLatestAutomationRunLoading(false);
      }
    }
  }, [selectedTradingAccountId, symbol]);

  const fetchBrokerInstrumentCatalog = useCallback(async () => {
    try {
      const brokersRes = await getBrokers();
      const options = instrumentOptionsFromBrokerCatalog(
        Array.isArray(brokersRes.data) ? brokersRes.data : [],
      );
      setBrokerCatalogOptions(options);
    } catch (error) {
      console.error("Error fetching broker instrument catalog:", error);
      setBrokerCatalogOptions([]);
    }
  }, []);

  useEffect(() => {
    if (!selectedTradingAccountId || !symbol) {
      latestAutomationRunScopeRef.current = "";
      latestAutomationRunSignatureRef.current = "__uninitialized__";
      setLatestAutomationRun(null);
      setLatestAutomationRunLoading(false);
      return undefined;
    }

    fetchLatestAutomationRun(selectedTradingAccountId, symbol);
    const pollMs = getDashboardPollIntervalMs(refreshRateMs);
    const intervalId = window.setInterval(() => {
      fetchLatestAutomationRun(selectedTradingAccountId, symbol);
    }, pollMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchLatestAutomationRun, refreshRateMs, selectedTradingAccountId, symbol]);

  const fetchStrategyLibrary = useCallback(async () => {
    try {
      const [visualRes, customRes, catalogRes, accountLinkRes] = await Promise.all([
        getStrategies().catch(() => ({ data: [] })),
        getCustomStrategies().catch(() => ({ data: { strategies: [] } })),
        getStrategyCatalog().catch(() => ({ data: { strategies: [] } })),
        getAccountStrategies().catch(() => ({ data: [] })),
      ]);

      setVisualStrategies(Array.isArray(visualRes.data) ? visualRes.data : []);
      setCustomStrategies(customRes.data?.strategies || []);
      setCatalogStrategies(catalogRes.data?.strategies || []);
      setAccountStrategyLinks(Array.isArray(accountLinkRes.data) ? accountLinkRes.data : []);
    } catch (error) {
      console.error("Error fetching strategy library:", error);
    }
  }, []);

  const fetchQuoteSnapshot = useCallback(async (targetSymbol = symbol) => {
    const normalizedTargetSymbol = normalizeInstrumentSymbol(targetSymbol);
    if (!normalizedTargetSymbol) {
      return null;
    }

    try {
      const response = await getQuote({ symbol: normalizedTargetSymbol });
      const payload = response.data || {};
      const nextPrice = Number(payload.current_price || payload.quote?.price || 0);
      const nextBid = Number(payload.bid || payload.quote?.bid || 0);
      const nextAsk = Number(payload.ask || payload.quote?.ask || 0);
      const nextHigh = Number(payload.high || payload.quote?.high || 0);
      const nextLow = Number(payload.low || payload.quote?.low || 0);
      const referencePrice = firstFiniteNumber(
        normalizedTargetSymbol === normalizeInstrumentSymbol(selectedSymbolRef.current)
          ? latestChartClose
          : null,
        positionEntryReferenceBySymbol[normalizedTargetSymbol],
        lastDisplayedPriceRef.current,
      );
      const compatibleQuote = isCompatibleLivePrice(nextPrice, referencePrice);

      if (compatibleQuote) {
        mergePositionMark(normalizedTargetSymbol, nextPrice);
      }

      if (normalizedTargetSymbol === normalizeInstrumentSymbol(selectedSymbolRef.current)) {
        if (compatibleQuote && Number.isFinite(nextPrice) && nextPrice > 0) {
          setCurrentPrice(nextPrice);
          if (dayOpenPrice > 0) {
            setPriceChange(((nextPrice - dayOpenPrice) / dayOpenPrice) * 100);
          }
          lastDisplayedPriceRef.current = nextPrice;
        }
 else if (!compatibleQuote) {
          const fallbackDisplayPrice = firstFiniteNumber(
            latestChartClose,
            positionEntryReferenceBySymbol[normalizedTargetSymbol],
          );
          if (Number.isFinite(fallbackDisplayPrice) && fallbackDisplayPrice > 0) {
            setCurrentPrice(fallbackDisplayPrice);
            mergePositionMark(normalizedTargetSymbol, fallbackDisplayPrice);
            lastDisplayedPriceRef.current = fallbackDisplayPrice;
          }
        }
        if (compatibleQuote && Number.isFinite(nextBid) && nextBid > 0) {
          setBidPrice(nextBid);
        }
        if (compatibleQuote && Number.isFinite(nextAsk) && nextAsk > 0) {
          setAskPrice(nextAsk);
        }
        if (compatibleQuote && Number.isFinite(nextHigh) && nextHigh > 0) {
          setDayHigh(nextHigh);
        }
        if (compatibleQuote && Number.isFinite(nextLow) && nextLow > 0) {
          setDayLow(nextLow);
        }
      }

      return payload;
    } catch (error) {
      console.debug("Error refreshing quote snapshot:", error);
      return null;
    }
  }, [latestChartClose, positionEntryReferenceBySymbol, symbol]);

  const getDisplayedPositionMark = useCallback((position) => {
    const normalizedPositionSymbol = normalizeInstrumentSymbol(position?.symbol);
    const normalizedSelectedSymbol = normalizeInstrumentSymbol(symbol);
    const entryPrice = firstFiniteNumber(position?.entry_price);
    const side = String(position?.side || "BUY").toUpperCase();
    if (!normalizedPositionSymbol) {
      return null;
    }

    const brokerStyleSelectedMark = normalizedPositionSymbol === normalizedSelectedSymbol
      ? firstFiniteNumber(
          side === "SELL" ? askPrice : bidPrice,
          currentPrice,
          positionMarksBySymbol[normalizedPositionSymbol],
        )
      : null;

    const explicitMark = normalizedPositionSymbol === normalizedSelectedSymbol
      ? brokerStyleSelectedMark
      : firstFiniteNumber(positionMarksBySymbol[normalizedPositionSymbol]);

    const markReference = firstFiniteNumber(
      normalizedPositionSymbol === normalizedSelectedSymbol ? latestChartClose : null,
      entryPrice,
      positionEntryReferenceBySymbol[normalizedPositionSymbol],
    );

    if (explicitMark !== null && explicitMark > 0 && isCompatibleLivePrice(explicitMark, markReference)) {
      return explicitMark;
    }

    const pnl = firstFiniteNumber(position?.pnl);
    const quantityValue = firstFiniteNumber(position?.quantity);
    const pointValue = firstFiniteNumber(position?.point_value);
    if (
      pnl === null
      || entryPrice === null
      || quantityValue === null
      || pointValue === null
      || entryPrice <= 0
      || quantityValue <= 0
      || pointValue <= 0
    ) {
      return null;
    }

    const direction = side === "SELL" ? -1 : 1;
    return entryPrice + pnl / (pointValue * quantityValue * direction);
  }, [
    askPrice,
    bidPrice,
    currentPrice,
    latestChartClose,
    positionEntryReferenceBySymbol,
    positionMarksBySymbol,
    symbol,
  ]);

  const getDisplayedPositionPnl = useCallback((position) => {
    const fallbackPnl = firstFiniteNumber(position?.pnl, 0) ?? 0;
    const liveMark = getDisplayedPositionMark(position);
    const entryPrice = firstFiniteNumber(position?.entry_price);
    const quantityValue = firstFiniteNumber(position?.quantity);
    const pointValue = firstFiniteNumber(position?.point_value);
    if (
      liveMark === null
      || entryPrice === null
      || quantityValue === null
      || pointValue === null
      || liveMark <= 0
      || entryPrice <= 0
      || quantityValue <= 0
      || pointValue <= 0
    ) {
      return fallbackPnl;
    }

    const side = String(position?.side || "BUY").toUpperCase();
    const direction = side === "SELL" ? -1 : 1;
    return (liveMark - entryPrice) * pointValue * quantityValue * direction;
  }, [getDisplayedPositionMark]);

  // Single source of truth for a position's P&L across the whole dashboard.
  // The broker-fed pnl-stream value always wins; the locally derived figure is
  // only a fallback for positions the stream has not reported yet. Every
  // surface (chart badge, day P&L, unrealized total, positions panel) must go
  // through this, otherwise the same position shows different numbers in
  // different places — which is exactly how the chart once displayed +$44.50
  // against a broker-confirmed -$117.50.
  // Decision history for the chart audit lane. Deliberately conservative:
  // one request per scope change plus a slow poll, a cancelled flag so a
  // late response from a previous scope can never overwrite the current one,
  // and failures degrade to an inline message rather than clearing the lane.
  useEffect(() => {
    if (activeTab !== "chart") return undefined;

    let cancelled = false;
    const params = { hours: 24, limit: 300 };
    if (selectedTradingAccountId) params.trading_account_id = selectedTradingAccountId;
    if (symbol) params.symbol = symbol;

    const load = async () => {
      if (!cancelled) {
        setDecisionTimeline((current) => ({ ...current, loading: true }));
      }
      try {
        const res = await getDecisionTimeline(params);
        if (cancelled) return;
        const data = res?.data || {};
        setDecisionTimeline({
          decisions: Array.isArray(data.decisions) ? data.decisions : [],
          counts: data.counts || {},
          windowHours: data.window_hours || 24,
          loading: false,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        setDecisionTimeline((current) => ({
          ...current,
          loading: false,
          error: error?.response?.status ? `HTTP ${error.response.status}` : "request failed",
        }));
      }
    };

    load();
    // Decisions are written at most once per automation cycle, so a 60s poll
    // is ample and keeps this well clear of the broker rate budget.
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeTab, selectedTradingAccountId, symbol]);

  useEffect(() => {
    if (activeTab !== "chart" || !symbol) return undefined;

    let cancelled = false;
    const load = async () => {
      if (!cancelled) setTimeframeLadder((current) => ({ ...current, loading: true }));
      try {
        const res = await getTimeframeLadder(symbol);
        if (cancelled) return;
        const data = res?.data || {};
        setTimeframeLadder({
          timeframes: Array.isArray(data.timeframes) ? data.timeframes : [],
          priorDay: data.prior_day || {},
          available: data.available !== false,
          loading: false,
        });
      } catch (error) {
        if (cancelled) return;
        // Context only - never blank the workspace because this failed.
        setTimeframeLadder({ timeframes: [], priorDay: {}, available: false, loading: false });
      }
    };

    load();
    // Higher timeframes change slowly; 2 minutes is plenty and stays well
    // clear of the broker rate budget.
    const timer = setInterval(load, 120000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeTab, symbol]);

  const resolvePositionPnl = useCallback((position) => {
    if (!position) return 0;
    const streamed = realtimePositionPnls[position.id];
    if (streamed !== undefined && streamed !== null && Number.isFinite(Number(streamed))) {
      return Number(streamed);
    }
    return firstFiniteNumber(getDisplayedPositionPnl(position), 0) ?? 0;
  }, [getDisplayedPositionPnl, realtimePositionPnls]);

  const selectedSymbolPositions = useMemo(() => {
    const normalizedSelectedSymbol = normalizeInstrumentSymbol(symbol);
    return positions.filter(
      (position) =>
        normalizeInstrumentSymbol(position?.symbol) === normalizedSelectedSymbol,
    );
  }, [positions, symbol]);

  const selectedPositionChartData = useMemo(() => {
    // Protection-overlay construction lives in lib/positionChartOverlays so
    // the confirmed-vs-intended stop logic is unit-tested rather than buried
    // in a memo. See that module for the reasoning.
    const firstTime = chartCandles[0]?.timestamp;
    const lastTime = chartCandles[chartCandles.length - 1]?.timestamp;
    return buildPositionChartData(selectedSymbolPositions, firstTime, lastTime);
  }, [
    chartCandles,
    selectedSymbolPositions,
  ]);

  // Closed trades for the current scope (account + symbol). Without the
  // account filter, switching accounts leaves the previous account's T-markers
  // on the chart and crowds the current view with foreign trades.
  const selectedSymbolTrades = useMemo(() => {
    const normalizedSym = normalizeInstrumentSymbol(symbol);
    const accountFilter = selectedTradingAccountId || null;
    return (trades || []).filter((t) => {
      if (normalizeInstrumentSymbol(t?.symbol) !== normalizedSym) return false;
      if (String(t?.status || "").toLowerCase() !== "closed") return false;
      if (accountFilter && t?.trading_account_id && t.trading_account_id !== accountFilter) {
        return false;
      }
      return true;
    });
  }, [trades, symbol, selectedTradingAccountId]);

  // Sorted ascending bar epoch-seconds derived from chart candles.
  // Extracted separately so the O(n log n) sort only re-runs when candles change,
  // not when trade selection changes.
  const chartBarTimes = useMemo(() => {
    if (!chartCandles?.length) return [];
    return chartCandles
      .map((c) => {
        const t = c?.timestamp ? parseDateValue(c.timestamp)?.getTime() : NaN;
        return Number.isFinite(t) ? Math.floor(t / 1000) : null;
      })
      .filter((t) => t !== null)
      .sort((a, b) => a - b);
  }, [chartCandles]);

  // Entry + exit markers for closed trades overlaid on the chart.
  // Trade timestamps (e.g. 12:47:23) rarely match bar open times (e.g. 12:46:00),
  // so we snap each trade time to the most recent chart candle bar.
  const selectedTradesChartData = useMemo(() => {
    const markers = [];
    if (!chartBarTimes.length || !selectedSymbolTrades.length) {
      return { markers };
    }

    const firstBar = chartBarTimes[0];
    const lastBar = chartBarTimes[chartBarTimes.length - 1];

    const snapToBar = (ts) => {
      if (!ts) return null;
      const parsed = parseDateValue(ts);
      const epoch = parsed ? Math.floor(parsed.getTime() / 1000) : NaN;
      if (!Number.isFinite(epoch) || epoch < firstBar || epoch > lastBar) return null;
      // Find largest bar time <= epoch (binary search floor)
      let lo = 0;
      let hi = chartBarTimes.length - 1;
      let best = chartBarTimes[0];
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (chartBarTimes[mid] <= epoch) {
          best = chartBarTimes[mid];
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return best;
    };

    const tradeMarkerSourceLabel = (trade) => {
      const mode = String(trade?.execution_mode || "").toLowerCase();
      const source = String(trade?.execution_source || "").toLowerCase();
      if (mode === "paper") return "paper";
      if (source === "broker_history") return "history";
      if (source === "reconciliation") return "adopted";
      if (mode === "live" && (trade?.broker_position_id || trade?.broker_order_id)) return "broker";
      if (mode === "live") return "live";
      return source || mode || "trade";
    };
    const tradeMarkerKind = (trade) => {
      const mode = String(trade?.execution_mode || "").toLowerCase();
      const source = String(trade?.execution_source || "").toLowerCase();
      if (mode === "paper") return "local_paper";
      if (source === "broker_history") return "backfill";
      if (source === "reconciliation") return "adopted";
      if (mode === "live" && (trade?.broker_position_id || trade?.broker_order_id)) return "broker_confirmed";
      return "trade_history";
    };

    selectedSymbolTrades.forEach((trade, idx) => {
      const side = String(trade?.side || "").toUpperCase();
      const n = idx + 1;
      const sourceLabel = tradeMarkerSourceLabel(trade);
      const markerKind = tradeMarkerKind(trade);
      const entryBar = snapToBar(trade.entry_time);
      if (entryBar) {
        markers.push({
          id: `trade-entry-${trade.id || idx}`,
          time: entryBar,
          position: side === "BUY" ? "belowBar" : "aboveBar",
          shape: side === "BUY" ? "arrowUp" : "arrowDown",
          color: "#60a5fa",
          text: `T${n} ${sourceLabel} in`,
          source: sourceLabel,
          kind: markerKind,
          trade_id: trade.id,
          broker_order_id: trade.broker_order_id,
          broker_position_id: trade.broker_position_id,
        });
      }
      const exitBar = snapToBar(trade.exit_time);
      if (exitBar) {
        const pnl = Number(trade.pnl);
        markers.push({
          id: `trade-exit-${trade.id || idx}`,
          time: exitBar,
          position: side === "BUY" ? "aboveBar" : "belowBar",
          shape: "circle",
          color: Number.isFinite(pnl) ? (pnl >= 0 ? "#4ade80" : "#f87171") : "#a78bfa",
          text: `T${n} ${sourceLabel} out`,
          source: sourceLabel,
          kind: markerKind,
          trade_id: trade.id,
          broker_order_id: trade.broker_order_id,
          broker_position_id: trade.broker_position_id,
        });
      }
    });
    return { markers };
  }, [selectedSymbolTrades, chartBarTimes]);

  const combinedChartOverlays = useMemo(
    () => [...strategyChartData.overlays, ...selectedPositionChartData.overlays],
    [selectedPositionChartData.overlays, strategyChartData.overlays],
  );

  const combinedChartMarkers = useMemo(
    () => [
      ...strategyChartData.markers,
      ...selectedPositionChartData.markers,
      ...selectedTradesChartData.markers,
    ],
    [selectedPositionChartData.markers, selectedTradesChartData.markers, strategyChartData.markers],
  );

  const chartPriceLines = useMemo(() => {
    const nextLines = [];

    // Current market value line
    if (currentPrice > 0 && chartCandles.length > 0) {
      const lastCandle = chartCandles[chartCandles.length - 1];
      const isBullish = Number(priceChange) > 0
        || (Number(priceChange) === 0 && Number(lastCandle?.close || 0) >= Number(lastCandle?.open || 0));

      nextLines.push({
        id: "current-price-line",
        price: currentPrice,
        color: isBullish ? "rgba(34, 197, 94, 0.88)" : "rgba(239, 68, 68, 0.88)",
        title: "",
        lineWidth: 1,
        lineStyle: "sparseDotted",
        axisLabelVisible: true,
        lineVisible: true,
        badgeVisible: false,
      });
    }

    selectedSymbolPositions.forEach((position) => {
      // Prefer the real-time pnl-stream value (calculated from live broker quotes
      // on the backend) over the locally-derived mark. The streaming value is
      // always consistent with what the Positions panel shows and avoids the
      // stale-bid-price problem where SignalR cached a price before rate-limiting
      // disconnected the feed.
      const livePnl = resolvePositionPnl(position);
      const side = String(position?.side || "BUY").toUpperCase();
      const entryPrice = firstFiniteNumber(position?.entry_price);
      if (entryPrice === null || entryPrice <= 0) {
        return;
      }
      nextLines.push({
        id: `chart-position-price-${position.id}`,
        price: entryPrice,
        color: livePnl >= 0 ? "rgba(34, 197, 94, 0.8)" : "rgba(239, 68, 68, 0.8)",
        title: "",
        lineWidth: 1,
        lineStyle: "solid",
        axisLabelVisible: true,
        lineVisible: true,
        badgeVisible: true,
        badgeAnchor: "right",
        badgeRightInset: 176,
        badgePnl: formatChartBadgePnlValue(livePnl),
        badgeTone: livePnl >= 0 ? "positive" : "negative",
        badgeQuantity: `${side === "SELL" ? "-" : ""}${Math.max(1, Number(position?.quantity) || 1)}`,
        badgeQuantityTone: side === "SELL" ? "negative" : "positive",
        badgeTrailing: "×",
        badgeTrailingTone: "neutral",
        closeId: position.id,
      });
    });

    return nextLines;
  }, [
    chartCandles,
    currentPrice,
    priceChange,
    formatChartBadgePnlValue,
    resolvePositionPnl,
    realtimePositionPnls,
    selectedSymbolPositions,
  ]);

  const displayedDayPnl = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const realizedTodayPnl = trades.reduce((sum, trade) => {
      const exitTime = trade?.exit_time || trade?.closed_at || trade?.created_at;
      const exitDate = exitTime ? new Date(exitTime) : null;
      if (!exitDate || Number.isNaN(exitDate.getTime()) || exitDate < startOfToday) {
        return sum;
      }
      if (String(trade?.status || "").toLowerCase() !== "closed") {
        return sum;
      }
      return sum + (firstFiniteNumber(trade?.pnl, 0) ?? 0);
    }, 0);

    const liveOpenPnl = positions.reduce(
      (sum, position) => sum + resolvePositionPnl(position),
      0,
    );

    const derivedDayPnl = realizedTodayPnl + liveOpenPnl;
    const shouldTrustBrokerDailyPnl = Boolean(
      account?.is_connected
      && selectedTradingAccountId
      && !displayedPaperMode,
    );

    if (shouldTrustBrokerDailyPnl) {
      if (Math.abs(effectiveDailyPnl) > 0.005) {
        return effectiveDailyPnl;
      }
      return Math.abs(derivedDayPnl) > 0.005 ? derivedDayPnl : effectiveDailyPnl;
    }
    
    // Prioritize our real-time derived P&L if it has any meaningful value
    if (Math.abs(derivedDayPnl) > 0.005) {
      return derivedDayPnl;
    }
    
    // Fallback to broker value
    return effectiveDailyPnl;
  }, [
    account?.is_connected,
    displayedPaperMode,
    effectiveDailyPnl,
    resolvePositionPnl,
    positions,
    selectedTradingAccountId,
    trades,
  ]);

  const displayedUnrealizedPnl = useMemo(() => {
    const derivedOpenPnl = positions.reduce(
      (sum, position) => sum + resolvePositionPnl(position),
      0,
    );

    const liveBrokerScope = Boolean(
      account?.is_connected
      && selectedTradingAccountId
      && !displayedPaperMode,
    );

    if (liveBrokerScope && Math.abs(derivedOpenPnl) > 0.005) {
      return derivedOpenPnl;
    }

    if (Math.abs(realtimeUnrealizedPnl) > 0.005) {
      return realtimeUnrealizedPnl;
    }

    return Math.abs(derivedOpenPnl) > 0.005 ? derivedOpenPnl : 0;
  }, [
    account?.is_connected,
    displayedPaperMode,
    resolvePositionPnl,
    positions,
    realtimeUnrealizedPnl,
    selectedTradingAccountId,
  ]);

  // Get trend color
  const trendColor = useMemo(() => {
    if (trend.includes("BULLISH") || trend === "NEUTRAL-BULL") return "text-green-500";
    if (trend.includes("BEARISH") || trend === "NEUTRAL-BEAR") return "text-red-500";
    return "text-zinc-400";
  }, [trend]);

  const trendBg = useMemo(() => {
    if (trend.includes("BULLISH") || trend === "NEUTRAL-BULL") return "bg-green-500/10";
    if (trend.includes("BEARISH") || trend === "NEUTRAL-BEAR") return "bg-red-500/10";
    return "bg-zinc-500/10";
  }, [trend]);

  // Get RSI state
  const rsiState = indicators.rsi > 70 ? "OVERBOUGHT" : indicators.rsi < 30 ? "OVERSOLD" : "NEUTRAL";
  const rsiColor = rsiState === "OVERBOUGHT" ? "text-red-500" : rsiState === "OVERSOLD" ? "text-green-500" : "text-blue-500";

  // Lifecycle Sync (useEffect for refs/storage)
  useEffect(() => {
    liveQuoteActiveRef.current = wsConnected && recentQuotes.length > 0;
  }, [recentQuotes.length, wsConnected]);

  useEffect(() => {
    selectedSymbolRef.current = symbol;
  }, [symbol]);

  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  useEffect(() => {
    marketDataSnapshotRef.current = {
      scopeKey: marketDataMeta.scopeKey || getMarketDataScopeKey(symbol, TIMEFRAMES[timeframe] || 5),
      source: marketDataMeta.source,
      candleCount: Array.isArray(candles) ? candles.length : 0,
    };
  }, [candles, marketDataMeta.scopeKey, marketDataMeta.source, symbol, timeframe]);

  useEffect(() => {
    localStorage.setItem("tp_chart_symbol", symbol);
  }, [symbol]);

  useEffect(() => {
    localStorage.setItem("tp_chart_timeframe", timeframe);
  }, [timeframe]);

  useEffect(() => {
    localStorage.setItem("tp_chart_strategy_ref", selectedStrategyRef || "none");
  }, [selectedStrategyRef]);

  useEffect(() => {
    localStorage.setItem("tp_chart_prefs", JSON.stringify(chartPrefs));
  }, [chartPrefs]);

  useEffect(() => {
    if (selectedTradingAccountId) {
      localStorage.setItem("tp_dashboard_account_id", selectedTradingAccountId);
    } else {
      localStorage.removeItem("tp_dashboard_account_id");
    }
  }, [selectedTradingAccountId]);

  useEffect(() => {
    if (selectedStrategyRef !== "none" && !strategyOptions.some((option) => option.value === selectedStrategyRef)) {
      setSelectedStrategyRef("none");
      setAppliedChartStrategy(null);
    }
  }, [selectedStrategyRef, strategyOptions]);

  useEffect(() => {
    if (!availableInstrumentOptions.length) {
      return;
    }
    const scopeKey = selectedTradingAccountId || "none";
    const currentState = symbolSelectionStateRef.current;
    const scopeChanged = currentState.scopeKey !== scopeKey;
    const symbolIsAvailable = availableInstrumentOptions.some((option) => option.value === symbol);
    const firstPositionSymbol = positions
      .map((position) => inferSymbolFromRaw(
        position?.symbol
        || position?.broker_contract_label
        || position?.broker_contract_id
        || "",
      ))
      .find((candidate) => availableInstrumentOptions.some((option) => option.value === candidate));
    const fallbackSymbol = availableInstrumentOptions[0]?.value || "";
    const nextDefaultSymbol = firstPositionSymbol || fallbackSymbol;

    if (!nextDefaultSymbol) {
      return;
    }

    if (scopeChanged) {
      setSymbol(nextDefaultSymbol);
      symbolSelectionStateRef.current = {
        scopeKey,
        manual: false,
        positionResolved: Boolean(firstPositionSymbol),
      };
      return;
    }

    if (!symbol || !symbolIsAvailable) {
      setSymbol(nextDefaultSymbol);
      symbolSelectionStateRef.current = {
        ...currentState,
        scopeKey,
        positionResolved: currentState.positionResolved || Boolean(firstPositionSymbol),
      };
      return;
    }

    if (!currentState.manual && !currentState.positionResolved && firstPositionSymbol && symbol !== firstPositionSymbol) {
      setSymbol(firstPositionSymbol);
      symbolSelectionStateRef.current = {
        ...currentState,
        scopeKey,
        positionResolved: true,
      };
    }
  }, [availableInstrumentOptions, positions, selectedTradingAccountId, symbol]);

  useEffect(() => {
    if (!selectedStrategyOption || selectedStrategyOption.value === "none") {
      setChartStrategyDraft(normalizeRuntimeDefinition(null, "TREND_FOLLOWING", "runtime", "dashboard"));
      return;
    }

    setChartStrategyDraft(
      normalizeRuntimeDefinition(
        selectedStrategyOption.runtime_definition,
        selectedStrategyOption.runtime_definition?.engine || "TREND_FOLLOWING",
        selectedStrategyOption.runtime_definition?.language || "runtime",
        selectedStrategyOption.runtime_definition?.source || "dashboard",
      ),
    );
  }, [selectedStrategyOption]);

  useEffect(() => {
    // Auto-sync only when account/symbol scope changes or linked strategy reference changes.
    // This prevents overriding manual dropdown selection in the same scope.
    const scopeKey = `${selectedTradingAccountId || "none"}::${symbol}`;
    const shouldSyncScope = strategySyncCursorRef.current.scopeKey !== scopeKey;
    const shouldSyncLinked = strategySyncCursorRef.current.linkedRef !== (linkedStrategyRef || "none");
    if (!shouldSyncScope && !shouldSyncLinked) {
      return;
    }

    if (linkedStrategyRef && linkedStrategyRef !== "none") {
      const linkedOption = strategyOptions.find((option) => option.value === linkedStrategyRef);
      if (!linkedOption) {
        // Strategy list may still be loading; retry on next options update.
        return;
      }

      const runtime = normalizeRuntimeDefinition(
        linkedOption.runtime_definition,
        linkedOption.runtime_definition?.engine || "TREND_FOLLOWING",
        linkedOption.runtime_definition?.language || "runtime",
        linkedOption.runtime_definition?.source || "dashboard",
      );
      setSelectedStrategyRef(linkedStrategyRef);
      setAppliedChartStrategy({
        ref: linkedStrategyRef,
        label: linkedOption.label || "Strategy Overlay",
        runtime,
      });
      strategySyncCursorRef.current = {
        scopeKey,
        linkedRef: linkedStrategyRef,
      };
      return;
    }

    setSelectedStrategyRef("none");
    setAppliedChartStrategy(null);
    setStrategyInputsOpen(false);
    strategySyncCursorRef.current = {
      scopeKey,
      linkedRef: "none",
    };
  }, [selectedTradingAccountId, symbol, linkedStrategyRef, strategyOptions]);

  useEffect(() => {
    if (!symbol) {
      return;
    }
    latestMarketRequestRef.current += 1;
    const nextScopeKey = getMarketDataScopeKey(symbol, TIMEFRAMES[timeframe] || 5);
    setCandles([]);
    setIndicators({});
    setSignals([]);
    setStrategyChartData({
      overlays: [],
      markers: [],
      summary: null,
      signals: [],
      indicatorSnapshot: null,
      paritySignature: null,
      dataQuality: null,
    });
    setMarketDataMeta({
      scopeKey: nextScopeKey,
      source: "loading",
      count: 0,
      firstTimestamp: null,
      lastTimestamp: null,
      dataQuality: null,
    });
    marketDataSnapshotRef.current = {
      scopeKey: nextScopeKey,
      source: "loading",
      candleCount: 0,
    };
  }, [symbol, timeframe, selectedTradingAccountId, appliedChartStrategy?.ref]);

  const activeChartStrategyEngine = appliedChartStrategy?.runtime?.engine || chartStrategyDraft.engine;

  const handleManualSymbolChange = useCallback((nextSymbol) => {
    symbolSelectionStateRef.current = {
      scopeKey: selectedTradingAccountId || "none",
      manual: true,
      positionResolved: true,
    };
    setSymbol(nextSymbol);
  }, [selectedTradingAccountId]);

  useEffect(() => {
    // Auto-sync only when account/symbol scope changes or linked strategy reference changes.
    // This prevents overriding manual dropdown selection in the same scope.
    const scopeKey = `${selectedTradingAccountId || "none"}::${symbol}`;
    const shouldSyncScope = strategySyncCursorRef.current.scopeKey !== scopeKey;
    const shouldSyncLinked = strategySyncCursorRef.current.linkedRef !== (linkedStrategyRef || "none");
    if (!shouldSyncScope && !shouldSyncLinked) {
      return;
    }

    if (linkedStrategyRef && linkedStrategyRef !== "none") {
      const linkedOption = strategyOptions.find((option) => option.value === linkedStrategyRef);
      if (!linkedOption) {
        // Strategy list may still be loading; retry on next options update.
        return;
      }

      const runtime = normalizeRuntimeDefinition(
        linkedOption.runtime_definition,
        linkedOption.runtime_definition?.engine || "TREND_FOLLOWING",
        linkedOption.runtime_definition?.language || "runtime",
        linkedOption.runtime_definition?.source || "dashboard",
      );
      setSelectedStrategyRef(linkedStrategyRef);
      setAppliedChartStrategy({
        ref: linkedStrategyRef,
        label: linkedOption.label || "Strategy Overlay",
        runtime,
      });
      strategySyncCursorRef.current = {
        scopeKey,
        linkedRef: linkedStrategyRef,
      };
      return;
    }

    setSelectedStrategyRef("none");
    setAppliedChartStrategy(null);
    setStrategyInputsOpen(false);
    strategySyncCursorRef.current = {
      scopeKey,
      linkedRef: "none",
    };
  }, [selectedTradingAccountId, symbol, linkedStrategyRef, strategyOptions]);

  const updateChartStrategyParam = useCallback((key, value) => {
    setChartStrategyDraft((current) => ({
      ...current,
      params: {
        ...current.params,
        [key]: value,
      },
    }));
  }, []);

  const persistAlertStrategyPreference = useCallback(async (strategyRef) => {
    try {
      await updateAlertPreferences({
        strategy_ref: strategyRef === "none" ? null : strategyRef,
      });
    } catch (error) {
      console.error("Error saving alert preference:", error);
    }
  }, []);

  // Toggle auto-trading
  const toggleAutoTrading = useCallback(async (checked) => {
    try {
      if (selectedTradingAccountId) {
        const nextChecked = typeof checked === "boolean" ? checked : !scopeAutoTradeEnabled;
        if (nextChecked && selectedStrategyRef === "none" && !selectedScopeHasActiveLink) {
          toast.error(
            `Link a strategy to ${selectedTradingAccount?.account_name || "this account"} for ${selectedInstrumentOption?.label || symbol} before enabling auto-trade.`,
          );
          return;
        }

        const payload = {
          trading_account_id: selectedTradingAccountId,
          symbol,
          enabled: nextChecked,
          strategy_ref: nextChecked && selectedStrategyRef !== "none" ? selectedStrategyRef : undefined,
          paper_mode: nextChecked ? displayedPaperMode : undefined,
          broker_contract_label: selectedInstrumentOption?.label || symbol,
          broker_contract_id: activeAccountContractLink?.broker_contract_id || undefined,
          interval_minutes: selectedIntervalMinutes,
        };
        const scopeRes = await toggleAutoTradeScope(payload);

        const enabledAccountIds = Array.isArray(scopeRes.data?.enabled_account_ids)
          ? scopeRes.data.enabled_account_ids
          : [];
        setAutoTradeEnabled(Boolean(scopeRes.data?.global_auto_trade_enabled));
        setPaperMode(Boolean(scopeRes.data?.global_paper_mode));
        setAutoTradeAccountIds(enabledAccountIds);
        setScopeAutoTradeEnabled(Boolean(scopeRes.data?.effective_auto_trade_enabled));
        setScopePaperMode(Boolean(scopeRes.data?.effective_paper_mode));

        await fetchStrategyLibrary();
        toast.success(
          nextChecked ? "Auto-trading enabled for this account + instrument." : "Auto-trading disabled for this account + instrument.",
        );
        return;
      }

      const newStatus = typeof checked === "boolean" ? checked : !autoTradeEnabled;
      const res = await toggleAutoTrade({
        enabled: newStatus,
        paper_mode: paperMode
      });
      setAutoTradeEnabled(Boolean(res.data.enabled));
      setPaperMode(Boolean(res.data.paper_mode));
      setAutoTradeAccountIds(Array.isArray(res.data.enabled_account_ids) ? res.data.enabled_account_ids : []);
      setScopeAutoTradeEnabled(Boolean(res.data.enabled));
      setScopePaperMode(Boolean(res.data.paper_mode));
      toast.success(`Auto-trading ${newStatus ? "enabled" : "disabled"}`);
    } catch (error) {
      console.error("Error toggling auto-trading:", error);
      toast.error(getApiErrorMessage(error, "Failed to toggle auto-trading"));
    }
  }, [
    activeAccountContractLink?.broker_contract_id,
    autoTradeEnabled,
    displayedPaperMode,
    fetchStrategyLibrary,
    paperMode,
    scopeAutoTradeEnabled,
    selectedInstrumentOption?.label,
    selectedScopeHasActiveLink,
    selectedStrategyRef,
    selectedIntervalMinutes,
    selectedTradingAccount?.account_name,
    selectedTradingAccountId,
    symbol,
  ]);

  // Toggle "TradoPro Takes Control" (adopt external positions)
  const toggleAdoptExternalTrades = useCallback(async (checked) => {
    if (!selectedTradingAccountId) return;
    const next = typeof checked === "boolean" ? checked : !adoptExternalTrades;
    adoptTradesUpdatingRef.current = true;
    setAdoptExternalTrades(next);
    try {
      await patchAdoptExternalTrades(selectedTradingAccountId, next);
      setAccount((prev) => prev ? { ...prev, adopt_external_trades: next } : prev);
      toast.success(`TradoPro Takes Control ${next ? "enabled" : "disabled"}`);
    } catch (error) {
      setAdoptExternalTrades(!next);
      toast.error(getApiErrorMessage(error, "Failed to update position adoption"));
    } finally {
      adoptTradesUpdatingRef.current = false;
    }
  }, [adoptExternalTrades, selectedTradingAccountId]);

  // Toggle paper mode
  const togglePaperMode = useCallback(async () => {
    try {
      if (selectedTradingAccountId && !autoTradeEnabledForSelectedAccount) {
        toast.error("Enable auto-trade with an active linked strategy before setting paper mode.");
        return;
      }
      const baseMode = selectedTradingAccountId ? paperModeForSelectedScope : paperMode;
      const newMode = !baseMode;
      const res = await setAutoTradePaperMode(selectedTradingAccountId ? {
        trading_account_id: selectedTradingAccountId,
        symbol,
        paper_mode: newMode,
      } : {
        paper_mode: newMode,
      });
      setAutoTradeEnabled(Boolean(res.data.enabled));
      setPaperMode(Boolean(res.data.paper_mode));
      setAutoTradeAccountIds(Array.isArray(res.data.enabled_account_ids) ? res.data.enabled_account_ids : []);
      if (selectedTradingAccountId) {
        setScopeAutoTradeEnabled(Boolean(res.data.effective_auto_trade_enabled));
        setScopePaperMode(Boolean(res.data.effective_paper_mode));
      } else {
        setScopeAutoTradeEnabled(Boolean(res.data.enabled));
        setScopePaperMode(Boolean(res.data.paper_mode));
      }
      toast.success(`Paper trading ${newMode ? "enabled" : "disabled"}`);
    } catch (error) {
      console.error("Error toggling paper mode:", error);
      toast.error(getApiErrorMessage(error, "Failed to toggle paper mode"));
    }
  }, [
    autoTradeEnabledForSelectedAccount,
    paperMode,
    paperModeForSelectedScope,
    selectedTradingAccountId,
    symbol,
  ]);

  // Fetch signal alerts
  const fetchSignalAlerts = useCallback(async () => {
    try {
      const params = {
        symbol,
        interval_minutes: selectedIntervalMinutes,
      };
      if (selectedTradingAccountId) {
        params.trading_account_id = selectedTradingAccountId;
      }
      if (selectedStrategyRef !== "none") {
        params.strategy_ref = selectedStrategyRef;
      }
      const res = await getAlerts(params);
      setSignalAlerts(res.data.alerts || []);
    } catch (error) {
      console.error("Error fetching signal alerts:", error);
    }
  }, [selectedIntervalMinutes, selectedStrategyRef, selectedTradingAccountId, symbol]);

  // Fetch signal analysis
  // Start/stop signal scanning
  const toggleSignalScanning = useCallback(async () => {
    try {
      const newStatus = !scanningSignals;
      await toggleSignalScan({
        enabled: newStatus,
        symbol
      });
      setScanningSignals(newStatus);
      toast.success(`Signal scanning ${newStatus ? "started" : "stopped"}`);
    } catch (error) {
      console.error("Error toggling signal scanning:", error);
      toast.error("Failed to toggle signal scanning");
    }
  }, [scanningSignals, symbol]);

  const subscribeToSelectedContract = useCallback((nextContractId) => {
    if (!nextContractId) {
      return;
    }

    quoteUnsubscribeRef.current?.();
    depthUnsubscribeRef.current?.();

    quoteUnsubscribeRef.current = projectXWS.subscribeToQuotes(nextContractId, (quote) => {
      lastQuoteReceivedAtRef.current = Date.now();
      liveQuoteActiveRef.current = true;

      const nextPrice = Number(quote.price || 0);
      if (Number.isFinite(nextPrice) && nextPrice > 0) {
        setCurrentPrice(nextPrice);
        mergePositionMark(selectedSymbolRef.current, nextPrice);
        if (dayOpenPrice > 0) {
          setPriceChange(((nextPrice - dayOpenPrice) / dayOpenPrice) * 100);
        }
        lastDisplayedPriceRef.current = nextPrice;
      }
      const nextBid = Number(quote.bid || 0);
      if (Number.isFinite(nextBid) && nextBid > 0) {
        setBidPrice(nextBid);
      }

      const nextAsk = Number(quote.ask || 0);
      if (Number.isFinite(nextAsk) && nextAsk > 0) {
        setAskPrice(nextAsk);
      }

      setRecentQuotes((prev) => {
        const newQuotes = [...prev, {
          time: quote.timestamp || quote.time || quote.tradeTime || new Date().toISOString(),
          price: quote.price,
          bid: quote.bid,
          ask: quote.ask,
        }];
        return newQuotes.slice(-240);
      });
    });

    depthUnsubscribeRef.current = projectXWS.subscribeToMarketDepth(nextContractId, (depth) => {
      const compatibleDepthHigh = Number(depth.high || 0);
      if (
        Number.isFinite(compatibleDepthHigh)
        && compatibleDepthHigh > 0
        && isCompatibleLivePrice(compatibleDepthHigh, lastDisplayedPriceRef.current)
      ) {
        setDayHigh(compatibleDepthHigh);
      }

      const compatibleDepthLow = Number(depth.low || 0);
      if (
        Number.isFinite(compatibleDepthLow)
        && compatibleDepthLow > 0
        && isCompatibleLivePrice(compatibleDepthLow, lastDisplayedPriceRef.current)
      ) {
        setDayLow(compatibleDepthLow);
      }

      if (depth.volume) setVolume(depth.volume);
    });
  }, []);

  const initializeRealtimeFeed = useCallback(async () => {
    try {
      const realtimeRes = await getRealtimeConfig(selectedTradingAccountId
        ? { trading_account_id: selectedTradingAccountId }
        : undefined);

      if (!realtimeRes.data?.success) {
        setWsConnected(false);
        return;
      }

      projectXWS.setToken(localStorage.getItem('token'));
      projectXWS.setAccountId(realtimeRes.data.accountId || null);
      setContractMap(realtimeRes.data.contractMap || {});

      const connected = await projectXWS.connect();
      setWsConnected(Boolean(connected));
      if (!connected) {
        liveQuoteActiveRef.current = false;
        return;
      }

      const nextContractId = (realtimeRes.data.contractMap || {})[symbol] || CONTRACT_MAP[symbol];
      if (currentContractRef.current && currentContractRef.current !== nextContractId) {
        await projectXWS.unsubscribe(currentContractRef.current);
      }
      currentContractRef.current = nextContractId || null;
      setRecentQuotes([]);
      liveQuoteActiveRef.current = false;
      lastQuoteReceivedAtRef.current = 0;
      subscribeToSelectedContract(nextContractId);
    } catch (error) {
      console.error("WebSocket initialization error:", error);
      setWsConnected(false);
      liveQuoteActiveRef.current = false;
    }
  }, [selectedTradingAccountId, subscribeToSelectedContract]);

  useEffect(() => {
    let cancelled = false;

    const bootRealtimeFeed = async () => {
      await initializeRealtimeFeed();
      if (cancelled) {
        await projectXWS.disconnect();
      }
    };

    bootRealtimeFeed();

    return () => {
      cancelled = true;
      quoteUnsubscribeRef.current?.();
      depthUnsubscribeRef.current?.();
      if (accountEventRefreshTimeoutRef.current) {
        window.clearTimeout(accountEventRefreshTimeoutRef.current);
        accountEventRefreshTimeoutRef.current = null;
      }
      void projectXWS.disconnect();
      setWsConnected(false);
    };
  }, [initializeRealtimeFeed]);

  // Update WebSocket subscription when symbol changes
  useEffect(() => {
    if (!wsConnected) {
      setRecentQuotes([]);
      liveQuoteActiveRef.current = false;
      lastQuoteReceivedAtRef.current = 0;
      return;
    }

    const nextContractId = contractMap[symbol] || CONTRACT_MAP[symbol];
    if (currentContractRef.current && currentContractRef.current !== nextContractId) {
      projectXWS.unsubscribe(currentContractRef.current);
    }
    currentContractRef.current = nextContractId || null;
    setRecentQuotes([]);
    liveQuoteActiveRef.current = false;
    lastQuoteReceivedAtRef.current = 0;
    subscribeToSelectedContract(nextContractId);
  }, [contractMap, subscribeToSelectedContract, symbol, wsConnected]);

  // Fetch initial data
  const fetchMarketData = useCallback(async () => {
    // Skip if a previous fetch is still in flight to prevent request pileup
    if (marketFetchInFlightRef.current) return;
    marketFetchInFlightRef.current = true;

    const interval = TIMEFRAMES[timeframe] || 5;
    const requestScopeKey = getMarketDataScopeKey(symbol, interval);
    const requestedBarCount = getTemporaryChartBarCount(interval);
    const shouldApplyStrategy = Boolean(appliedChartStrategy?.ref && appliedChartStrategy.ref !== "none");
    const requestId = latestMarketRequestRef.current + 1;
    latestMarketRequestRef.current = requestId;

    try {
      const marketDataParams = shouldPreferBrokerMarketData ? { prefer_live: true } : {};
      const [candlesRes, indicatorsRes] = await Promise.all([
        getCandles(symbol, interval, requestedBarCount, marketDataParams),
        getIndicators(symbol, interval, requestedBarCount, marketDataParams),
      ]);
      if (requestId !== latestMarketRequestRef.current) {
        return;
      }
      const nextCandles = candlesRes.data?.candles || [];
      const candleQuote = candlesRes.data?.quote || {};
      const candleDataSource = candlesRes.data?.data_source || "unknown";
      const currentSnapshot = marketDataSnapshotRef.current;
      const shouldIgnoreSimulatedFlicker = (
        currentSnapshot.scopeKey === requestScopeKey
        && candleDataSource === "simulated"
        && currentSnapshot.candleCount > 0
        && currentSnapshot.candleCount >= getStickyMarketDataFloor(interval)
        && marketDataSourcePriority(currentSnapshot.source) > marketDataSourcePriority(candleDataSource)
      );
      if (shouldIgnoreSimulatedFlicker) {
        return;
      }

      // Prevent unnecessary re-renders that can cause chart flicker.
      // Only update candles state when the payload actually changed.
      setCandles((prev) => {
        if (!prev || prev.length === 0) return nextCandles;
        if (!nextCandles || nextCandles.length === 0) return nextCandles;

        const pFirst = prev[0];
        const nFirst = nextCandles[0];
        const pLast = prev[prev.length - 1];
        const nLast = nextCandles[nextCandles.length - 1];

        const same =
          prev.length === nextCandles.length &&
          pFirst?.timestamp === nFirst?.timestamp &&
          pFirst?.open === nFirst?.open &&
          pFirst?.high === nFirst?.high &&
          pFirst?.low === nFirst?.low &&
          pFirst?.close === nFirst?.close &&
          pFirst?.volume === nFirst?.volume &&
          pLast?.timestamp === nLast?.timestamp &&
          pLast?.open === nLast?.open &&
          pLast?.high === nLast?.high &&
          pLast?.low === nLast?.low &&
          pLast?.close === nLast?.close &&
          pLast?.volume === nLast?.volume;

        return same ? prev : nextCandles;
      });
      setMarketDataMeta({
        scopeKey: requestScopeKey,
        source: candleDataSource,
        count: nextCandles.length,
        firstTimestamp: nextCandles[0]?.timestamp || null,
        lastTimestamp: nextCandles[nextCandles.length - 1]?.timestamp || null,
        dataQuality: candlesRes.data?.data_quality || null,
      });
      marketDataSnapshotRef.current = {
        scopeKey: requestScopeKey,
        source: candleDataSource,
        candleCount: nextCandles.length,
      };

      // Pulse the fetch indicator
      if (dataFetchPulseTimerRef.current) clearTimeout(dataFetchPulseTimerRef.current);
      setDataFetchPulse(true);
      dataFetchPulseTimerRef.current = setTimeout(() => setDataFetchPulse(false), 600);

      const quoteFeedFresh = Boolean(
        wsConnected
        && lastQuoteReceivedAtRef.current
        && (Date.now() - lastQuoteReceivedAtRef.current) < QUOTE_STALE_MS
      );

      if (nextCandles.length > 0 && !quoteFeedFresh) {
        const latest = nextCandles[nextCandles.length - 1];
        const dayOpen = nextCandles[0]?.open || latest.open;
        const nextClose = Number(candleQuote.price || latest.close || 0);
        if (Number.isFinite(dayOpen) && dayOpen > 0) {
          setDayOpenPrice(dayOpen);
        }
        if (Number.isFinite(nextClose) && nextClose > 0) {
          setCurrentPrice(nextClose);
          lastDisplayedPriceRef.current = nextClose;
          if (dayOpen > 0) {
            setPriceChange(((nextClose - dayOpen) / dayOpen) * 100);
          }
        }
        const nextBid = Number(candleQuote.bid || candlesRes.data?.bid || 0);
        if (Number.isFinite(nextBid) && nextBid > 0) {
          setBidPrice(nextBid);
        }
        const nextAsk = Number(candleQuote.ask || candlesRes.data?.ask || 0);
        if (Number.isFinite(nextAsk) && nextAsk > 0) {
          setAskPrice(nextAsk);
        }
        const nextHigh = Number(candleQuote.high || candlesRes.data?.high || 0);
        if (Number.isFinite(nextHigh) && nextHigh > 0) {
          setDayHigh(nextHigh);
        }
        const nextLow = Number(candleQuote.low || candlesRes.data?.low || 0);
        if (Number.isFinite(nextLow) && nextLow > 0) {
          setDayLow(nextLow);
        }
      }

      setIndicators(indicatorsRes.data?.indicators || {});
      setSignals(indicatorsRes.data?.signals || []);

      let strategyChart = null;
      if (shouldApplyStrategy) {
        try {
          const strategyChartRes = await getStrategyChart({
            symbol,
            interval_minutes: interval,
            count: requestedBarCount,
            strategy: appliedChartStrategy.runtime?.engine,
            strategy_ref: appliedChartStrategy.ref,
            runtime: appliedChartStrategy.runtime,
            candles: nextCandles,
            data_source: candleDataSource,
          });
          if (requestId !== latestMarketRequestRef.current) {
            return;
          }
          strategyChart = strategyChartRes?.data || null;
        } catch (error) {
          console.error("Error fetching strategy chart:", error);
        }
      }

      if (strategyChart) {
        const normalizedStrategyOverlays = Array.isArray(strategyChart.chart?.overlays)
          ? strategyChart.chart.overlays.map((overlay) => ({
              ...overlay,
              affectsScale: false,
            }))
          : [];
        setStrategyChartData({
          overlays: normalizedStrategyOverlays,
          markers: strategyChart.chart?.signal_markers || [],
          summary: strategyChart.summary || null,
          signals: strategyChart.signals || [],
          indicatorSnapshot: strategyChart.indicator_snapshot || null,
          paritySignature: strategyChart.parity_signature || null,
          dataQuality: strategyChart.data_quality || null,
        });
      } else if (!shouldApplyStrategy) {
        setStrategyChartData({
          overlays: [],
          markers: [],
          summary: null,
          signals: [],
          indicatorSnapshot: null,
          paritySignature: null,
          dataQuality: null,
        });
      }
    } catch (error) {
      console.error("Error fetching market data:", error);
      if (requestId !== latestMarketRequestRef.current) {
        return;
      }
      const errorStatus = error?.response?.status;
      const errorDetail = error?.response?.data?.detail
        || error?.response?.data?.error
        || error?.message
        || "Request failed";
      let errorSource = "fetch_failed";
      if (errorStatus === 401 || errorStatus === 403) {
        errorSource = "auth_failed";
      } else if (errorStatus === 503 || /topstepx/i.test(String(errorDetail))) {
        errorSource = "topstepx_unavailable";
      } else if (/no reliable market data/i.test(String(errorDetail))) {
        errorSource = "store_unavailable";
      }
      setIndicators({});
      setSignals([]);
      setMarketDataMeta({
        scopeKey: requestScopeKey,
        source: errorSource,
        count: 0,
        firstTimestamp: null,
        lastTimestamp: null,
        dataQuality: null,
        errorStatus: errorStatus ?? null,
        errorDetail: errorDetail ? String(errorDetail).slice(0, 200) : null,
        errorEndpoint: error?.config?.url || error?.request?.responseURL || "/trading/candles",
      });
      setStrategyChartData({
        overlays: [],
        markers: [],
        summary: null,
        signals: [],
        indicatorSnapshot: null,
        paritySignature: null,
        dataQuality: null,
      });
    } finally {
      marketFetchInFlightRef.current = false;
    }
  }, [symbol, timeframe, appliedChartStrategy, shouldPreferBrokerMarketData, wsConnected]);

  const applyStrategyToChart = useCallback(async () => {
    if (selectedStrategyRef === "none") {
      toast.error("Select a strategy overlay before applying.");
      setAppliedChartStrategy(null);
      setStrategyChartData({
        overlays: [],
        markers: [],
        summary: null,
        signals: [],
        indicatorSnapshot: null,
        paritySignature: null,
        dataQuality: null,
      });
      return;
    }

    const confirmationMessage = `Apply ${selectedStrategyOption?.label || "this strategy"} to ${selectedInstrumentOption?.label || symbol}${selectedTradingAccount?.account_name ? ` on ${selectedTradingAccount.account_name}` : ""}?`;
    const isConfirmed = typeof window?.confirm === "function"
      ? window.confirm(confirmationMessage)
      : true;
    if (!isConfirmed) {
      return;
    }

    if (selectedTradingAccountId) {
      try {
        const linkPayload = {
          trading_account_id: selectedTradingAccountId,
          strategy_id: selectedStrategyRef,
          contracts_to_trade: activeAccountContractLink?.contracts_to_trade || 1,
          contracts: [
            {
              symbol,
              broker_contract_label: selectedInstrumentOption?.label || symbol,
              contracts_to_trade: activeAccountContractLink?.contracts_to_trade || 1,
            },
          ],
          is_active: true,
        };
        const linkRes = await createAccountStrategy(linkPayload);
        const createdLinks = Array.isArray(linkRes.data?.links)
          ? linkRes.data.links
          : (linkRes.data?.id ? [linkRes.data] : []);
        if (createdLinks.length) {
          const merged = new Map(accountStrategyLinks.map((link) => [link.id, link]));
          createdLinks.forEach((link) => {
            if (link?.id) merged.set(link.id, link);
          });
          setAccountStrategyLinks(Array.from(merged.values()));
        } else {
          await fetchStrategyLibrary();
        }
        await fetchAutoTradeEffectiveState(selectedTradingAccountId, symbol);
      } catch (error) {
        console.error("Error saving strategy link during apply:", error);
        toast.error(getApiErrorMessage(error, "Failed to save strategy for this account + instrument"));
        return;
      }
    }

    setAppliedChartStrategy({
      ref: selectedStrategyRef,
      label: selectedStrategyOption?.label || "Strategy Overlay",
      runtime: chartStrategyDraft,
    });
    toast.success(
      selectedTradingAccountId
        ? `Applied and saved ${selectedStrategyOption?.label || "strategy"} for ${selectedTradingAccount?.account_name || "this account"} + ${selectedInstrumentOption?.label || symbol}.`
        : `Applied ${selectedStrategyOption?.label || "strategy"} to ${selectedInstrumentOption?.label || symbol}.`,
    );
  }, [
    activeAccountContractLink?.contracts_to_trade,
    accountStrategyLinks,
    chartStrategyDraft,
    fetchAutoTradeEffectiveState,
    fetchStrategyLibrary,
    selectedInstrumentOption?.label,
    selectedStrategyOption?.label,
    selectedStrategyRef,
    selectedTradingAccount?.account_name,
    selectedTradingAccountId,
    symbol,
  ]);

  const clearStrategyOverlay = useCallback(async () => {
    if (activeAccountContractLink?.id && activeAccountContractLink?.is_active) {
      try {
        await toggleAccountStrategy(activeAccountContractLink.id);
        const res = await getAccountStrategies();
        setAccountStrategyLinks(Array.isArray(res.data) ? res.data : []);
        toast.success(`Removed linked strategy for ${selectedInstrumentOption?.label || symbol}.`);
      } catch (err) {
        console.error("Failed to deactivate strategy link:", err);
        toast.error(getApiErrorMessage(err, "Unable to clear the linked strategy overlay"));
        return;
      }
    }

    setSelectedStrategyRef("none");
    setAppliedChartStrategy(null);
    setStrategyInputsOpen(false);
    setStrategyChartData({
      overlays: [],
      markers: [],
      summary: null,
      signals: [],
      indicatorSnapshot: null,
      paritySignature: null,
      dataQuality: null,
    });

    strategySyncCursorRef.current = {
      scopeKey: `${selectedTradingAccountId || "none"}::${symbol}`,
      linkedRef: "none",
    };

    if (!activeAccountContractLink?.id) {
      toast.success("Overlay cleared.");
    }
  }, [activeAccountContractLink, selectedInstrumentOption?.label, selectedTradingAccountId, symbol]);

  const handleSaveAutoTradeContracts = useCallback(async (nextQuantity) => {
    if (!activeAccountContractLink?.id) {
      toast.error(`Link a strategy to ${selectedInstrumentOption?.label || symbol} before setting automation quantity.`);
      return;
    }

    const quantity = Math.max(1, Number.parseInt(nextQuantity || 1, 10) || 1);
    setSavingAutoTradeContracts(true);
    try {
      const response = await updateAccountStrategySettings(activeAccountContractLink.id, {
        contracts_to_trade: quantity,
      });
      const updatedLink = response.data || {};
      setAccountStrategyLinks((current) => current.map((link) => (
        link.id === activeAccountContractLink.id
          ? {
              ...link,
              ...updatedLink,
              contracts_to_trade: Math.max(1, Number(updatedLink.contracts_to_trade || quantity) || quantity),
            }
          : link
      )));
      await fetchAutoTradeEffectiveState(selectedTradingAccountId, symbol);
      toast.success(`Automation quantity set to ${quantity} contract${quantity === 1 ? "" : "s"} for ${selectedInstrumentOption?.label || symbol}.`);
    } catch (error) {
      console.error("Failed to update automation quantity:", error);
      toast.error(getApiErrorMessage(error, "Unable to update automation quantity"));
    } finally {
      setSavingAutoTradeContracts(false);
    }
  }, [
    activeAccountContractLink?.id,
    fetchAutoTradeEffectiveState,
    selectedInstrumentOption?.label,
    selectedTradingAccountId,
    symbol,
  ]);

  // Fetch account and positions
  const fetchOrderDiagnostics = useCallback(async (accountIdOverride = null) => {
    const effectiveAccountId = accountIdOverride || selectedTradingAccountId || account?.account_id || null;
    const effectivePaperMode = selectedTradingAccountId ? scopePaperMode : paperMode;
    const scopeKey = getOrderDiagnosticsScopeKey(effectiveAccountId, symbol);
    const requestId = orderDiagnosticsRequestRef.current + 1;
    orderDiagnosticsRequestRef.current = requestId;
    if (!effectiveAccountId || effectivePaperMode) {
      setOrderDiagnostics(null);
      setOrderDiagnosticsLoading(false);
      return null;
    }

    setOrderDiagnosticsLoading(true);
    try {
      const response = await getOrderDiagnostics({
          trading_account_id: effectiveAccountId,
          symbol,
        });
      const payload = response.data || null;
      if (payload) {
        setOrderDiagnosticsByScope((current) => ({
          ...current,
          [scopeKey]: payload,
        }));
      }
      if (orderDiagnosticsRequestRef.current === requestId) {
        setOrderDiagnostics(payload);
      }
      return payload;
    } catch (error) {
      console.error("Error fetching order diagnostics:", error);
      return null;
    } finally {
      if (orderDiagnosticsRequestRef.current === requestId) {
        setOrderDiagnosticsLoading(false);
      }
    }
  }, [
    account?.account_id,
    paperMode,
    scopePaperMode,
    selectedTradingAccountId,
    symbol,
  ]);

  // Order entry hook handles form state
  const orderEntry = useOrderEntry({
    selectedTradingAccountId,
    activeAccountContractLink,
    symbol,
    displayedPaperMode,
    selectedTradingAccount: account, // Use 'account' state which holds full object
    refreshOrderDiagnostics: fetchOrderDiagnostics,
    // Callback handled in wrapper function below
  });

  const scheduleLiveAccountRefresh = useCallback(() => {
    if (accountEventRefreshTimeoutRef.current) {
      window.clearTimeout(accountEventRefreshTimeoutRef.current);
    }
    accountEventRefreshTimeoutRef.current = window.setTimeout(() => {
      fetchAccountSnapshot(selectedTradingAccountId);
      fetchAccountData();
      accountEventRefreshTimeoutRef.current = null;
    }, 350);
  }, [fetchAccountData, fetchAccountSnapshot, selectedTradingAccountId]);

  useEffect(() => {
    if (!wsConnected || !selectedExternalAccountId) {
      return undefined;
    }

    const handleAccountUpdate = (payload) => {
      const payloadAccountId = extractRealtimeAccountId(payload);
      if (!payloadAccountId || String(payloadAccountId) !== selectedExternalAccountId) {
        return;
      }

      const nextBalance = firstFiniteNumber(
        payload?.balance,
        payload?.accountBalance,
        payload?.cashBalance,
        payload?.netLiquidation,
        payload?.netLiq,
        payload?.equity,
      );
      const nextBuyingPower = firstFiniteNumber(
        payload?.buyingPower,
        payload?.buying_power,
        payload?.availableBuyingPower,
        payload?.marginAvailable,
      );
      const nextDailyPnl = extractRealtimeDailyPnl(payload);
      const shouldApplyRealtimeDailyPnl = nextDailyPnl !== null && Math.abs(nextDailyPnl) > 0.005;

      setAccount((current) => ({
        ...(current || {}),
        external_account_id: selectedExternalAccountId,
        balance: nextBalance ?? current?.balance ?? 0,
        buying_power: nextBuyingPower ?? current?.buying_power ?? 0,
        daily_pnl: shouldApplyRealtimeDailyPnl ? nextDailyPnl : (current?.daily_pnl ?? 0),
      }));

      setTradingAccounts((current) => current.map((entry) => {
        if (String(entry?.external_account_id || "") !== selectedExternalAccountId) {
          return entry;
        }
        return {
          ...entry,
          balance: nextBalance ?? entry.balance,
          buying_power: nextBuyingPower ?? entry.buying_power,
          daily_pnl: shouldApplyRealtimeDailyPnl ? nextDailyPnl : entry.daily_pnl,
        };
      }));
    };

    const handleBrokerStateUpdate = (payload) => {
      const payloadAccountId = extractRealtimeAccountId(payload);
      if (payloadAccountId && String(payloadAccountId) !== selectedExternalAccountId) {
        return;
      }
      scheduleLiveAccountRefresh();
    };

    const unsubscribeAccount = projectXWS.subscribe("account", handleAccountUpdate);
    const unsubscribePosition = projectXWS.subscribe("position", handleBrokerStateUpdate);
    const unsubscribeOrder = projectXWS.subscribe("order", handleBrokerStateUpdate);

    return () => {
      unsubscribeAccount?.();
      unsubscribePosition?.();
      unsubscribeOrder?.();
    };
  }, [
    scheduleLiveAccountRefresh,
    selectedExternalAccountId,
    wsConnected,
  ]);

  // Fetch AI analysis
  const fetchAIAnalysis = useCallback(async () => {
    setAiLoading(true);
    try {
      const res = await getAIAnalysis(symbol, {
        interval_minutes: TIMEFRAMES[timeframe] || 5,
      });
      setAiAnalysis(res.data);
      toast.success("AI analysis updated");
    } catch (error) {
      console.error("Error fetching AI analysis:", error);
      toast.error("Failed to fetch AI analysis");
    } finally {
      setAiLoading(false);
    }
  }, [symbol, timeframe]);

  const fetchNewsEvents = useCallback(async () => {
    setNewsLoading(true);
    try {
      const res = await getNewsEvents({ symbol, limit: 8 });
      setNewsEvents(Array.isArray(res.data?.events) ? res.data.events : []);
    } catch (error) {
      console.error("Error fetching market news events:", error);
      setNewsEvents([]);
    } finally {
      setNewsLoading(false);
    }
  }, [symbol]);

  // Place order wrapper
  const handlePlaceOrder = useCallback(async () => {
    try {
      const result = await orderEntry.placeOrder();
      if (result) {
        fetchAccountData();
        fetchOrderDiagnostics(selectedTradingAccountId || account?.account_id);
        if (result.execution_mode === "live") {
          if (orderDiagnosticsRefreshTimeoutRef.current) {
            window.clearTimeout(orderDiagnosticsRefreshTimeoutRef.current);
          }
          orderDiagnosticsRefreshTimeoutRef.current = window.setTimeout(() => {
            fetchOrderDiagnostics(selectedTradingAccountId || account?.account_id);
            orderDiagnosticsRefreshTimeoutRef.current = null;
          }, 4000);
        }
      }
    } catch (error) {
      // Hook already handles errors
    }
  }, [account?.account_id, fetchAccountData, fetchOrderDiagnostics, orderEntry, selectedTradingAccountId]);

  // Close position
  const closePosition = useCallback(async (positionId) => {
    try {
      await closePositionApi(positionId);
      toast.success("Position closed");
      fetchAccountData();
      fetchOrderDiagnostics();
    } catch (error) {
      console.error("Error closing position:", error);
      toast.error("Failed to close position");
    }
  }, [fetchAccountData, fetchOrderDiagnostics]);

  const flatAll = useCallback(async () => {
    try {
      const params = selectedTradingAccountId ? { trading_account_id: selectedTradingAccountId } : {};
      await flatAllApi(params);
      toast.success("Flatten all command issued");
      fetchAccountData();
      fetchOrderDiagnostics();
    } catch (error) {
      console.error("Error flattening positions:", error);
      toast.error("Failed to flatten positions");
    }
  }, [fetchAccountData, fetchOrderDiagnostics, selectedTradingAccountId]);

  const cancelOrder = useCallback(async (orderId) => {
    try {
      await cancelOrderApi(orderId);
      toast.success("Order cancelled");
      fetchAccountData();
      fetchOrderDiagnostics();
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error("Failed to cancel order");
    }
  }, [fetchAccountData, fetchOrderDiagnostics]);

  const handleSyncProtectiveStop = useCallback(async (accountId, symbol) => {
    try {
      await syncProtectiveStopApi(accountId, symbol);
      toast.success(`Synced protective stop for ${symbol}`);
      fetchAccountData();
    } catch (error) {
      console.error("Error syncing protective stop:", error);
      toast.error(getApiErrorMessage(error, "Failed to sync protective stop"));
    }
  }, [fetchAccountData]);

  const handleReProtectPosition = useCallback(async (accountId, symbol) => {
    try {
      await reProtectPositionApi(accountId, symbol);
      toast.success(`Successfully placed protective stop for ${symbol}`);
      fetchAccountData();
    } catch (error) {
      console.error("Error re-protecting position:", error);
      toast.error(getApiErrorMessage(error, "Failed to re-protect position"));
    }
  }, [fetchAccountData]);

  const fetchInitialData = useCallback(async () => {
    const settleWithinBootWindow = (promise) => Promise.race([
      Promise.resolve(promise),
      new Promise((resolve) => {
        setTimeout(() => resolve({ __bootTimeout: true }), DASHBOARD_BOOT_TIMEOUT_MS);
      }),
    ]);

    try {
      await Promise.allSettled([
        settleWithinBootWindow(fetchAccountData()),
        settleWithinBootWindow(fetchBrokerInstrumentCatalog()),
        settleWithinBootWindow(fetchStrategyLibrary()),
        settleWithinBootWindow(fetchAutoTradeStatus()),
        settleWithinBootWindow(getSignalAnalysis()),
      ]);
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [
    fetchAccountData,
    fetchAutoTradeStatus,
    fetchBrokerInstrumentCatalog,
    fetchStrategyLibrary,
    getSignalAnalysis,
  ]);

  // Initial data load
  useEffect(() => {
    if (initialLoadStartedRef.current) {
      return;
    }
    initialLoadStartedRef.current = true;
    fetchInitialData();
  }, [fetchInitialData]);

  // Scope-aware default selection: once tradingAccounts, accountStrategyLinks, and
  // autoTradeAccountIds are all loaded, prefer the active auto-trade scope (if any)
  // over localStorage. This runs once per mount; manual user changes win afterward.
  //
  // Selection priority:
  //   1. First entry in autoTradeAccountIds that parses as `accountId::symbol`
  //      AND has an active strategy link for that account+symbol.
  //   2. First active strategy link (account + primary_symbol) if no scope match.
  //   3. localStorage / first-account / first-position (existing behavior).
  useEffect(() => {
    if (scopeDefaultsAppliedRef.current) return;
    if (!Array.isArray(tradingAccounts) || !tradingAccounts.length) return;
    if (!Array.isArray(accountStrategyLinks)) return;

    const activeLinks = accountStrategyLinks.filter((link) => link?.is_active);
    if (!activeLinks.length && !(autoTradeAccountIds && autoTradeAccountIds.length)) {
      return; // nothing actionable; wait for data or fall through to legacy defaults
    }

    let preferredAccountId = null;
    let preferredSymbol = null;
    let preferredLink = null;

    // 1. Active auto-trade scope wins
    for (const rawScope of (autoTradeAccountIds || [])) {
      if (typeof rawScope !== "string" || !rawScope.includes("::")) continue;
      const [accountId, rawSymbol] = rawScope.split("::");
      const candidateAccount = (accountId || "").trim();
      const candidateSymbol = normalizeInstrumentSymbol((rawSymbol || "").trim());
      if (!candidateAccount || !candidateSymbol) continue;
      const accountExists = tradingAccounts.some((a) => a.id === candidateAccount);
      if (!accountExists) continue;
      const matchingLink = activeLinks.find(
        (link) =>
          link.trading_account_id === candidateAccount &&
          normalizeInstrumentSymbol(link.primary_symbol || "") === candidateSymbol,
      );
      preferredAccountId = candidateAccount;
      preferredSymbol = candidateSymbol;
      preferredLink = matchingLink || null;
      break;
    }

    // 2. Fall back to first active strategy link
    if (!preferredAccountId && activeLinks.length) {
      const firstLink = activeLinks.find((link) =>
        tradingAccounts.some((a) => a.id === link.trading_account_id),
      );
      if (firstLink) {
        preferredAccountId = firstLink.trading_account_id;
        preferredSymbol = normalizeInstrumentSymbol(firstLink.primary_symbol || "");
        preferredLink = firstLink;
      }
    }

    if (!preferredAccountId || !preferredSymbol) {
      return; // legacy fallbacks (localStorage / first account) already handle this
    }

    // Apply
    if (preferredAccountId !== selectedTradingAccountId) {
      setSelectedTradingAccountId(preferredAccountId);
    }
    if (preferredSymbol !== symbol) {
      setSymbol(preferredSymbol);
      symbolSelectionStateRef.current = {
        scopeKey: preferredAccountId,
        manual: false,
        positionResolved: true,
      };
    }
    // Linked strategy ref will be picked up by the existing strategySyncCursor effect
    // once accountStrategyLinks/strategyOptions are settled. We do not setSelectedStrategyRef
    // directly here to avoid racing with that effect.
    scopeDefaultsAppliedRef.current = true;
  }, [tradingAccounts, accountStrategyLinks, autoTradeAccountIds, selectedTradingAccountId, symbol]);

  useEffect(() => {
    fetchSignalAlerts();
  }, [fetchSignalAlerts]);

  useEffect(() => {
    fetchAutoTradeEffectiveState();
  }, [fetchAutoTradeEffectiveState, selectedTradingAccountId, symbol, accountStrategyLinks]);

  useEffect(() => {
    if (!selectedTradingAccountId || !scopeAutoTradeEnabled || !selectedScopeHasActiveLink) {
      return undefined;
    }
    const syncKey = `${selectedTradingAccountId}:${symbol}:${selectedIntervalMinutes}`;
    if (autoTradeIntervalSyncRef.current === syncKey) {
      return undefined;
    }
    autoTradeIntervalSyncRef.current = syncKey;

    let cancelled = false;
    const syncScopeInterval = async () => {
      try {
        await setAutoTradeScopeInterval({
          trading_account_id: selectedTradingAccountId,
          symbol,
          interval_minutes: selectedIntervalMinutes,
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Error syncing auto-trade interval:", error);
          autoTradeIntervalSyncRef.current = "";
        }
      }
    };
    syncScopeInterval();

    return () => {
      cancelled = true;
    };
  }, [
    scopeAutoTradeEnabled,
    selectedScopeHasActiveLink,
    selectedTradingAccountId,
    selectedIntervalMinutes,
    symbol,
  ]);

  useEffect(() => {
    if (!selectedTradingAccountId) {
      return;
    }
    fetchAccountSnapshot(selectedTradingAccountId);
  }, [fetchAccountSnapshot, selectedTradingAccountId]);

  useEffect(() => {
    fetchOrderDiagnostics();
  }, [fetchOrderDiagnostics]);

  useEffect(() => {
    persistAlertStrategyPreference(selectedStrategyRef);
  }, [persistAlertStrategyPreference, selectedStrategyRef]);

  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  // Update signal analysis when symbol changes
  useEffect(() => {
    getSignalAnalysis();
  }, [symbol]);

  useEffect(() => {
    if (activeTab === "news") {
      fetchNewsEvents();
    }
  }, [activeTab, fetchNewsEvents]);

  // Refresh data periodically (fallback when WebSocket not connected)
  useEffect(() => {
    const pollMs = getDashboardPollIntervalMs(refreshRateMs);

    const quoteFallbackInterval = setInterval(() => {
      const marketState = projectXWS.getState();
      const marketHubConnected = marketState.market === "Connected";
      const quoteFeedFresh = Boolean(
        marketHubConnected
        && lastQuoteReceivedAtRef.current
        && (Date.now() - lastQuoteReceivedAtRef.current) < QUOTE_STALE_MS
      );

      liveQuoteActiveRef.current = quoteFeedFresh;

      if (!marketHubConnected || !quoteFeedFresh) {
        fetchQuoteSnapshot();
      }
    }, pollMs);

    // Always poll candles at the admin-controlled refresh rate so the chart
    // stays up-to-date even when the WebSocket is connected (WS only delivers
    // individual quotes, not aggregated OHLCV bars).
    const marketInterval = setInterval(() => {
      fetchMarketData();
    }, pollMs);

    const snapshotInterval = setInterval(() => {
      if (selectedTradingAccountId) {
        fetchAccountSnapshot(selectedTradingAccountId);
      }
    }, pollMs);

    const accountInterval = setInterval(() => {
      fetchAccountData();
    }, pollMs);

    const orderDiagnosticsInterval = setInterval(() => {
      fetchOrderDiagnostics();
    }, pollMs);

    const positionMarkInterval = setInterval(() => {
      fetchPositionMarks();
    }, pollMs);

    return () => {
      clearInterval(quoteFallbackInterval);
      clearInterval(marketInterval);
      clearInterval(snapshotInterval);
      clearInterval(accountInterval);
      clearInterval(orderDiagnosticsInterval);
      clearInterval(positionMarkInterval);
    };
  }, [
    fetchAccountData,
    fetchAccountSnapshot,
    fetchMarketData,
    fetchOrderDiagnostics,
    fetchPositionMarks,
    fetchQuoteSnapshot,
    hasActiveSelectedSymbolPosition,
    refreshRateMs,
    selectedTradingAccountId,
    wsConnected,
  ]);

  useEffect(() => () => {
    if (orderDiagnosticsRefreshTimeoutRef.current) {
      window.clearTimeout(orderDiagnosticsRefreshTimeoutRef.current);
      orderDiagnosticsRefreshTimeoutRef.current = null;
    }
  }, []);

  // Navigate to strategies
  const goToStrategies = useCallback(() => {
    navigate("/settings?tab=strategies");
  }, [navigate]);

  // Navigate to auto-trade settings
  const goToAutoTradeSettings = useCallback(() => {
    navigate("/settings?tab=auto-trade");
  }, [navigate]);

  // Navigate to paper trading
  const goToPaperTrading = useCallback(() => {
    navigate("/settings?tab=paper-trading");
  }, [navigate]);

  // Navigate to live trading
  const goToLiveTrading = useCallback(() => {
    navigate("/settings?tab=live-trading");
  }, [navigate]);

  const handleAccountChange = useCallback((value) => {
    const nextAccountId = value === "none" ? "" : value;
    setSelectedTradingAccountId(nextAccountId);
    setAccount(null);
    setPositions([]);
    setTrades([]);
    if (!nextAccountId) {
      setScopeAutoTradeEnabled(Boolean(autoTradeEnabled));
      setScopePaperMode(Boolean(paperMode));
    }
  }, [autoTradeEnabled, paperMode]);

  const toggleVolume = useCallback(() => setChartPrefs((p) => ({ ...p, showVolume: !p.showVolume })), []);
  const toggleSMA = useCallback(() => setChartPrefs((p) => ({ ...p, showSMA: !p.showSMA })), []);
  const toggleEMA = useCallback(() => setChartPrefs((p) => ({ ...p, showEMA: !p.showEMA })), []);
  const toggleStrategyInputs = useCallback(() => setStrategyInputsOpen((current) => !current), []);

  useEffect(() => {
    if (isCompactLayout) return;
    setMobileChartControlsOpen(false);
    setMobileSymbolsExpanded(false);
  }, [isCompactLayout]);

  const mobilePrimaryInstrumentOptions = useMemo(() => {
    const seen = new Set();
    const selected = availableInstrumentOptions.find((option) => option.value === symbol);
    const primary = [];
    const pushOption = (option) => {
      if (!option || seen.has(option.value)) return;
      seen.add(option.value);
      primary.push(option);
    };
    pushOption(selected);
    availableInstrumentOptions.slice(0, 5).forEach(pushOption);
    return primary.slice(0, 6);
  }, [availableInstrumentOptions, symbol]);

  const mobileOverflowInstrumentOptions = useMemo(
    () => availableInstrumentOptions.filter((option) => !mobilePrimaryInstrumentOptions.some((entry) => entry.value === option.value)),
    [availableInstrumentOptions, mobilePrimaryInstrumentOptions],
  );

  const selectedContractLabel = activeAccountContractLink?.broker_contract_label
    || activeAccountContractLink?.primary_symbol
    || selectedInstrumentOption?.label
    || symbol;

  const renderDashboardTabs = (compact = false) => (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <div className={compact ? "overflow-x-auto pb-1" : ""}>
        <TabsList className={`dashboard-surface-tabs${compact ? " min-w-max" : ""}`}>
          <TabsTrigger value="chart" className="dashboard-surface-tab text-white">
            Chart
          </TabsTrigger>
          <TabsTrigger value="indicators" className="dashboard-surface-tab text-white">
            Indicators
          </TabsTrigger>
          <TabsTrigger value="signals" className="dashboard-surface-tab text-white">
            Signals
          </TabsTrigger>
          <TabsTrigger value="news" className="dashboard-surface-tab text-white">
            News
          </TabsTrigger>
          <TabsTrigger value="ai" className="dashboard-surface-tab text-white">
            AI
          </TabsTrigger>
        </TabsList>
      </div>
    </Tabs>
  );

  const renderChartWorkspace = (compact = false) => (
    <div className={`flex flex-col gap-3${compact ? "" : " h-full"}`}>
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className={compact ? "overflow-x-auto pb-1" : ""}>
          <div className="flex min-w-max items-center gap-1">
            {availableInstrumentOptions.map((option) => (
              <Tooltip key={option.value}>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant={symbol === option.value ? "default" : "outline"}
                    onClick={() => handleManualSymbolChange(option.value)}
                    className={symbol === option.value ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
                  >
                    {option.label}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="border-white/10 bg-black text-white">
                  {option.name}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="h-8 w-[90px] bg-white/5 border-white/10 text-white">
              <SelectValue placeholder="TF" />
            </SelectTrigger>
            <SelectContent className="bg-black border-white/10">
              {Object.keys(TIMEFRAMES).map((tf) => (
                <SelectItem key={tf} value={tf} className="text-white focus:bg-white/10">
                  {tf}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            size="sm"
            variant={showVolume ? "default" : "outline"}
            onClick={toggleVolume}
            className={showVolume ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
          >
            Vol
          </Button>
          <Button
            size="sm"
            variant={showSMA ? "default" : "outline"}
            onClick={toggleSMA}
            className={showSMA ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
          >
            SMA20
          </Button>
          <Button
            size="sm"
            variant={showEMA ? "default" : "outline"}
            onClick={toggleEMA}
            className={showEMA ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
          >
            EMA21
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
        <div className="flex flex-col gap-2.5">
          <div className={`flex gap-3 ${compact ? "flex-col" : "items-center"}`}>
            <div className="min-w-0 flex-1 overflow-x-auto">
              <div className="flex min-w-max items-center gap-2 pr-1">
                <Select value={selectedStrategyRef} onValueChange={setSelectedStrategyRef}>
                  <SelectTrigger className="h-8 w-[240px] bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Strategy overlay" />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10">
                    {strategyOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value} className="text-white focus:bg-white/10">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleStrategyInputs}
                  className="h-8 bg-white/5 border-white/10 px-3 text-white hover:bg-white/10"
                  disabled={selectedStrategyRef === "none"}
                >
                  {strategyInputsOpen ? <X className="mr-2 h-4 w-4" /> : <Sparkles className="mr-2 h-4 w-4" />}
                  Inputs
                </Button>
                <Button
                  size="sm"
                  onClick={applyStrategyToChart}
                  className="h-8 bg-blue-600 px-3 text-white hover:bg-blue-700"
                >
                  Apply Strategy
                </Button>
                {appliedChartStrategy ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearStrategyOverlay}
                    className="h-8 bg-white/5 border-white/10 px-3 text-white hover:bg-white/10"
                  >
                    Clear Overlay
                  </Button>
                ) : null}

                {activeChartStrategyRef !== "none" ? (
                  <>
                    <div className="mx-1 h-5 w-px bg-white/10" />
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">Strategy</span>
                    <Badge variant="secondary" className="h-6 shrink-0 bg-white/10 px-2 text-[11px] text-white border-white/20 normal-case tracking-normal">
                      {STRATEGY_ENGINES.find((engine) => engine.value === activeChartStrategyEngine)?.label || activeChartStrategyEngine}
                    </Badge>
                  </>
                ) : null}
              </div>
            </div>

            {activeChartStrategyRef !== "none" ? (() => {
              const summary = strategyChartData.summary || {};
              // Prefer regime `state` (current bar classification) over `action`
              // (transition signal). Action is only present on the bar where
              // a new signal fires; state reflects the ongoing regime.
              const effective = summary.state || summary.action || null;
              const stateSource = summary.state_source || null;
              const isFallback = typeof stateSource === "string" && stateSource.startsWith("previous_bar");
              const stateBarTimestamp = summary.state_bar_timestamp || summary.state_timestamp || null;
              const stateLabelTime = stateBarTimestamp
                ? formatChartTimestamp(stateBarTimestamp, chartTimeZone)
                : null;
              const effectiveLabel = formatSignalBadgeLabel(effective);
              return (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="secondary"
                      className={`h-6 shrink-0 border px-2 text-[11px] ${
                        effective === "BUY"
                          ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
                          : effective === "SELL"
                            ? "border-red-500/30 bg-red-500/15 text-red-300"
                            : "border-white/10 bg-white/5 text-white/70"
                      }`}
                      title={
                        isFallback
                          ? `Previous classified state: ${effectiveLabel} — not a fresh executable signal.${stateLabelTime ? ` Last classified bar ${stateLabelTime}.` : ""}`
                          : (stateLabelTime
                              ? `Live chart regime ${effectiveLabel} as of ${stateLabelTime}`
                              : undefined)
                      }
                    >
                      {effectiveLabel}
                      {isFallback ? <span className="ml-1 text-[9px] uppercase opacity-70">prev</span> : null}
                    </Badge>
                    {summary.action && summary.action !== summary.state ? (
                      <Badge
                        variant="secondary"
                        className="h-6 shrink-0 border border-amber-500/30 bg-amber-500/15 px-2 text-[10px] uppercase tracking-wider text-amber-200"
                        title={`Current executable signal — fresh ${formatSignalBadgeLabel(summary.action)} crossover on the latest bar.${summary.last_signal?.reason ? ` ${summary.last_signal.reason}.` : ""}`}
                      >
                        New {formatSignalBadgeLabel(summary.action)}
                      </Badge>
                    ) : null}
                    {stateLabelTime ? (
                      <span className="text-[10px] text-white/45">as of {stateLabelTime}</span>
                    ) : null}
                  </div>
                  {isFallback ? (
                    <span className="text-[10px] text-amber-200/70">
                      Chart state is from a previous bar — current bar is not classified. Not a fresh executable signal.
                    </span>
                  ) : null}
                </div>
              );
            })() : null}
          </div>

          {strategyInputsOpen && selectedStrategyRef !== "none" ? (
            <div className="rounded-md border border-white/10 bg-black/20 p-2.5">
              <StrategyRuntimeFields
                engine={chartStrategyDraft.engine}
                params={chartStrategyDraft.params}
                onChange={updateChartStrategyParam}
                gridClassName="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-5"
                fieldClassName="space-y-1 rounded-md border border-white/10 bg-black/30 px-2.5 py-2"
                labelClassName="text-[10px] font-medium uppercase tracking-[0.14em] text-white/55"
                inputClassName="h-8 border-white/10 bg-white/5 px-2 text-sm text-white"
                switchCardClassName="flex min-h-[54px] items-center justify-between rounded-md border border-white/10 bg-black/30 px-2.5 py-2"
                hintClassName="text-[10px] text-white/35"
                showDescriptions={false}
              />
            </div>
          ) : null}

          {appliedChartStrategy || displayedCandlePayload.count || displayedCandlePayload.source !== "unknown" ? (
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/60">
              <ChartTrustIndicator
                source={displayedCandlePayload.source}
                sourceLabel={formatMarketDataSourceLabel(displayedCandlePayload.source)}
                isErrorSource={isMarketDataErrorSource(displayedCandlePayload.source)}
                lastBarAt={displayedCandlePayload.lastTimestamp}
                dataQualityLabel={chartDataQuality ? formatDataQualityLabel(chartDataQuality) : null}
                paritySignature={strategyParitySignatureShort}
                errorDetails={[
                  marketDataMeta.errorEndpoint ? `Endpoint: ${marketDataMeta.errorEndpoint}` : null,
                  marketDataMeta.errorStatus ? `HTTP ${marketDataMeta.errorStatus}` : null,
                  marketDataMeta.errorDetail ? `Reason: ${marketDataMeta.errorDetail}` : null,
                ]}
              />
              {displayedCandlePayload.count ? <span>{displayedCandlePayload.count} bars</span> : null}
              {displayedCandlePayload.firstTimestamp ? (
                <span>From: {formatChartTimestamp(displayedCandlePayload.firstTimestamp, chartTimeZone)}</span>
              ) : null}
              {displayedCandlePayload.lastTimestamp ? (
                <span>To: {formatChartTimestamp(displayedCandlePayload.lastTimestamp, chartTimeZone)}</span>
              ) : null}
              {strategyChartData.summary ? <span>Score: {strategyChartData.summary.score ?? 0}</span> : null}
              {strategyChartData.indicatorSnapshot?.kama ? (
                <span>KAMA: {strategyChartData.indicatorSnapshot.kama}</span>
              ) : null}
              {strategyChartData.indicatorSnapshot?.atr ? (
                <span>ATR: {strategyChartData.indicatorSnapshot.atr}</span>
              ) : null}
              {combinedChartOverlays.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {combinedChartOverlays.map((overlay) => (
                    <div
                      key={overlay.id}
                      className="flex items-center gap-2 rounded-md border border-white/10 bg-black/20 px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-white/65"
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: overlay.color }} />
                      {overlay.label}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <TimeframeStrip
        symbol={symbol}
        timeframes={timeframeLadder.timeframes}
        priorDay={timeframeLadder.priorDay}
        available={timeframeLadder.available}
        loading={timeframeLadder.loading}
        activeTimeframe={timeframe}
        selectableTimeframes={Object.keys(TIMEFRAMES)}
        onSelectTimeframe={setTimeframe}
      />

      <div className={compact ? "h-[52vh] min-h-[360px] max-h-[620px]" : "flex-1 min-h-0"}>
        <CandlestickChart
          key={chartRenderKey}
          candles={chartCandles}
          indicators={chartIndicators}
          overlays={combinedChartOverlays}
          markers={combinedChartMarkers}
          priceLines={chartPriceLines}
          onBadgeClose={closePosition}
          signals={signals}
          symbol={symbol}
          wsConnected={wsConnected}
          showVolume={showVolume}
          timeZone={chartTimeZone}
          height="100%"
          width="100%"
        />
      </div>

      <DecisionLane
        decisions={decisionTimeline.decisions}
        counts={decisionTimeline.counts}
        windowHours={decisionTimeline.windowHours}
        loading={decisionTimeline.loading}
        error={decisionTimeline.error}
      />
    </div>
  );

  const renderActiveDashboardTab = (compact = false) => {
    if (activeTab === "chart") {
      return renderChartWorkspace(compact);
    }
    if (activeTab === "indicators") {
      return (
        <IndicatorsTab
          indicators={indicators}
          rsiColor={rsiColor}
          rsiState={rsiState}
          trend={trend}
          trendColor={trendColor}
        />
      );
    }
    if (activeTab === "signals") {
      return <SignalsTab displayedSignals={displayedSignals} />;
    }
    if (activeTab === "news") {
      return (
        <NewsTab
          newsEvents={newsEvents}
          newsLoading={newsLoading}
          fetchNewsEvents={fetchNewsEvents}
          symbol={symbol}
          appTimeZone={appTimeZone}
        />
      );
    }
    if (activeTab === "ai") {
      return (
        <AITab
          aiAnalysis={aiAnalysis}
          aiLoading={aiLoading}
          fetchAIAnalysis={fetchAIAnalysis}
        />
      );
    }
    return null;
  };

  const dashboardRenderBoundaryKey = `${activeTab}:${symbol}:${timeframe}:${chartRenderKey}`;
  const mobileRenderBoundaryKey = `${mobileSection}:${mobileInsightTab}:${symbol}:${timeframe}:${chartRenderKey}`;

  const renderMobileSectionTabs = () => (
    <div className="grid grid-cols-4 gap-1 rounded-lg border border-white/10 bg-black/25 p-1" aria-label="Dashboard view">
        {MOBILE_DASHBOARD_SECTIONS.map((section) => (
          <Button
            key={section.value}
            size="sm"
            variant={mobileSection === section.value ? "default" : "outline"}
            onClick={() => setMobileSection(section.value)}
            className={mobileSection === section.value
              ? "h-10 min-w-0 rounded-md bg-blue-600 px-1 text-xs hover:bg-blue-700"
              : "h-10 min-w-0 rounded-md border-0 bg-transparent px-1 text-xs text-white/70 hover:bg-white/10 hover:text-white"}
          >
            {section.label}
          </Button>
        ))}
    </div>
  );

  const renderMobileInsightTabs = () => (
    <div className="overflow-x-auto pb-1">
      <div className="flex min-w-max items-center gap-2">
        {MOBILE_INSIGHT_TABS.map((tab) => (
          <Button
            key={tab.value}
            size="sm"
            variant={mobileInsightTab === tab.value ? "default" : "outline"}
            onClick={() => setMobileInsightTab(tab.value)}
            className={mobileInsightTab === tab.value ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
          >
            {tab.label}
          </Button>
        ))}
      </div>
    </div>
  );

  const renderMobileAccountBar = () => (
    <MobileAccountScopeBar
      selectedTradingAccountId={selectedTradingAccountId}
      selectedTradingAccount={selectedTradingAccount}
      tradingAccounts={tradingAccounts}
      accountSelectValue={accountSelectValue}
      onAccountChange={handleAccountChange}
    />
  );

  const renderMobileTradingContextCard = () => (
    <Card className="-mx-3 rounded-none border-x-0 border-white/10 bg-white/[0.03]">
      <CardContent className="space-y-3 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">Trading scope</div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="border-white/20 bg-white/10 text-white">
              {wsConnected ? (
                <span className="flex items-center gap-1"><Wifi className="h-3 w-3 text-green-500" />Live</span>
              ) : (
                <span className="flex items-center gap-1"><WifiOff className="h-3 w-3 text-yellow-500" />Delayed</span>
              )}
            </Badge>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={loading ? "Refreshing market data" : "Refresh market data"}
              disabled={loading}
              onClick={fetchMarketData}
              className="h-8 w-8 border border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-white/45">Instrument</div>
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-max items-center gap-2">
              {mobilePrimaryInstrumentOptions.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={symbol === option.value ? "default" : "outline"}
                  onClick={() => handleManualSymbolChange(option.value)}
                  className={symbol === option.value ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
                >
                  {option.label}
                </Button>
              ))}
              {mobileOverflowInstrumentOptions.length ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMobileSymbolsExpanded((current) => !current)}
                  className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                >
                  {mobileSymbolsExpanded ? "Less" : `More (${mobileOverflowInstrumentOptions.length})`}
                </Button>
              ) : null}
            </div>
          </div>
          {mobileSymbolsExpanded && mobileOverflowInstrumentOptions.length ? (
            <div className="mt-2 flex flex-wrap gap-2 rounded-md border border-white/10 bg-black/20 p-2">
              {mobileOverflowInstrumentOptions.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={symbol === option.value ? "default" : "outline"}
                  onClick={() => handleManualSymbolChange(option.value)}
                  className={symbol === option.value ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
        {selectedTradingAccountId && (
          <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/20 px-3 py-2">
            <div className="min-w-0 pr-3">
              <span className="text-xs font-medium text-white">TradoPro Takes Control</span>
              <p className="truncate text-[11px] text-white/55">Manage positions opened outside TradoPro</p>
            </div>
            <Switch
              checked={Boolean(adoptExternalTrades)}
              onCheckedChange={toggleAdoptExternalTrades}
            />
          </div>
        )}
        {renderTradingTruthBar(true)}
      </CardContent>
    </Card>
  );

  const renderMobileChartControlsDrawer = () => (
    <Drawer open={mobileChartControlsOpen} onOpenChange={setMobileChartControlsOpen}>
      <DrawerContent className="max-h-[88vh] border-white/10 bg-[#090909] text-white">
        <DrawerHeader className="text-left">
          <DrawerTitle className="text-white">Chart Controls</DrawerTitle>
          <DrawerDescription className="text-white/60">
            Tune timeframe, overlays, and data details without crowding the phone view.
          </DrawerDescription>
        </DrawerHeader>
        <div className="overflow-y-auto px-4 pb-6">
          <div className="space-y-5">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="mb-3 text-[10px] uppercase tracking-[0.18em] text-white/45">Timeframe & Studies</div>
              <div className="space-y-3">
                <Select value={timeframe} onValueChange={setTimeframe}>
                  <SelectTrigger className="h-10 w-full bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Timeframe" />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10">
                    {Object.keys(TIMEFRAMES).map((tf) => (
                      <SelectItem key={tf} value={tf} className="text-white focus:bg-white/10">
                        {tf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={showVolume ? "default" : "outline"}
                    onClick={toggleVolume}
                    className={showVolume ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
                  >
                    Vol
                  </Button>
                  <Button
                    size="sm"
                    variant={showSMA ? "default" : "outline"}
                    onClick={toggleSMA}
                    className={showSMA ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
                  >
                    SMA20
                  </Button>
                  <Button
                    size="sm"
                    variant={showEMA ? "default" : "outline"}
                    onClick={toggleEMA}
                    className={showEMA ? "bg-blue-600 hover:bg-blue-700" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
                  >
                    EMA21
                  </Button>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="mb-3 text-[10px] uppercase tracking-[0.18em] text-white/45">Strategy Overlay</div>
              <div className="space-y-3">
                <Select value={selectedStrategyRef} onValueChange={setSelectedStrategyRef}>
                  <SelectTrigger className="h-10 w-full bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Strategy overlay" />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10">
                    {strategyOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value} className="text-white focus:bg-white/10">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleStrategyInputs}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                    disabled={selectedStrategyRef === "none"}
                  >
                    {strategyInputsOpen ? "Hide Inputs" : "Edit Inputs"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      applyStrategyToChart();
                      setMobileChartControlsOpen(false);
                    }}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Apply Strategy
                  </Button>
                  {appliedChartStrategy ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        clearStrategyOverlay();
                        setMobileChartControlsOpen(false);
                      }}
                      className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                    >
                      Clear Overlay
                    </Button>
                  ) : null}
                </div>

                {strategyInputsOpen && selectedStrategyRef !== "none" ? (
                  <div className="rounded-md border border-white/10 bg-black/20 p-3">
                    <StrategyRuntimeFields
                      engine={chartStrategyDraft.engine}
                      params={chartStrategyDraft.params}
                      onChange={updateChartStrategyParam}
                      gridClassName="grid grid-cols-1 gap-2"
                      fieldClassName="space-y-1 rounded-md border border-white/10 bg-black/30 px-2.5 py-2"
                      labelClassName="text-[10px] font-medium uppercase tracking-[0.14em] text-white/55"
                      inputClassName="h-9 border-white/10 bg-white/5 px-2 text-sm text-white"
                      switchCardClassName="flex min-h-[54px] items-center justify-between rounded-md border border-white/10 bg-black/30 px-2.5 py-2"
                      hintClassName="text-[10px] text-white/35"
                      showDescriptions={false}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="mb-3 text-[10px] uppercase tracking-[0.18em] text-white/45">Source Details</div>
              <div className="space-y-2 text-sm text-white/70">
                <div>Source: {formatMarketDataSourceLabel(displayedCandlePayload.source)}</div>
                <div>Quality: {formatDataQualityLabel(chartDataQuality)}</div>
                {strategyParitySignatureShort ? <div>Parity: {strategyParitySignatureShort}</div> : null}
                <div>Bars: {displayedCandlePayload.count || 0}</div>
                <div>From: {displayedCandlePayload.firstTimestamp ? formatChartTimestamp(displayedCandlePayload.firstTimestamp, chartTimeZone) : "n/a"}</div>
                <div>To: {displayedCandlePayload.lastTimestamp ? formatChartTimestamp(displayedCandlePayload.lastTimestamp, chartTimeZone) : "n/a"}</div>
                {strategyChartData.summary ? <div>Score: {strategyChartData.summary.score ?? 0}</div> : null}
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );

  const renderMobileChartWorkspace = () => (
    <Card className="rounded-lg border-white/10 bg-white/[0.03]">
      <CardContent className="space-y-3 p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">Chart</div>
            <div className="mt-1 text-sm font-semibold text-white">
              {selectedInstrumentOption?.label || symbol} • {timeframe}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMobileChartControlsOpen(true)}
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            Controls
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/60">
          <Badge
            variant="secondary"
            className={`h-6 border px-2 text-[11px] ${
              displayedCandlePayload.source?.startsWith("historical_store")
                ? "border-cyan-500/30 bg-cyan-500/15 text-cyan-200"
                : displayedCandlePayload.source === "topstepx"
                  ? "border-amber-500/30 bg-amber-500/15 text-amber-200"
                  : displayedCandlePayload.source === "simulated"
                    ? "border-fuchsia-500/30 bg-fuchsia-500/15 text-fuchsia-200"
                    : "border-white/10 bg-white/5 text-white/70"
            }`}
          >
            Source: {formatMarketDataSourceLabel(displayedCandlePayload.source)}
          </Badge>
          {chartDataQuality ? (
            <Badge
              variant="secondary"
              className={`h-6 border px-2 text-[11px] ${dataQualityBadgeClassName(chartDataQuality)}`}
            >
              {formatDataQualityLabel(chartDataQuality)}
            </Badge>
          ) : null}
          {strategyParitySignatureShort ? (
            <Badge variant="secondary" className="h-6 border border-white/10 bg-white/5 px-2 text-[11px] text-white">
              Parity {strategyParitySignatureShort}
            </Badge>
          ) : null}
          {activeChartStrategyRef !== "none" ? (
            <Badge variant="secondary" className="h-6 border border-white/10 bg-white/10 px-2 text-[11px] text-white">
              {selectedStrategyOption?.label || appliedChartStrategy?.label || "Strategy Overlay"}
            </Badge>
          ) : null}
          {strategyChartData.summary?.action || strategyChartData.summary?.state ? (
            <Badge
              variant="secondary"
              className={`h-6 border px-2 text-[11px] ${
                (strategyChartData.summary?.action || strategyChartData.summary?.state) === "BUY"
                  ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
                  : (strategyChartData.summary?.action || strategyChartData.summary?.state) === "SELL"
                    ? "border-red-500/30 bg-red-500/15 text-red-300"
                    : "border-white/10 bg-white/5 text-white/70"
              }`}
            >
              {formatSignalBadgeLabel(strategyChartData.summary?.action || strategyChartData.summary?.state)}
            </Badge>
          ) : null}
        </div>

        <TimeframeStrip
          symbol={symbol}
          timeframes={timeframeLadder.timeframes}
          priorDay={timeframeLadder.priorDay}
          available={timeframeLadder.available}
          loading={timeframeLadder.loading}
          activeTimeframe={timeframe}
          selectableTimeframes={Object.keys(TIMEFRAMES)}
          onSelectTimeframe={setTimeframe}
        />

        <div className="overflow-hidden border-y border-white/10 bg-black/30 sm:rounded-md sm:border-x">
          <div className="h-[58svh] min-h-[400px] max-h-[640px]">
            <CandlestickChart
              key={chartRenderKey}
              candles={chartCandles}
              indicators={chartIndicators}
              overlays={combinedChartOverlays}
              markers={combinedChartMarkers}
              priceLines={chartPriceLines}
              onBadgeClose={closePosition}
              signals={signals}
              symbol={symbol}
              wsConnected={wsConnected}
              showVolume={showVolume}
              timeZone={chartTimeZone}
              height="100%"
              width="100%"
            />
          </div>
        </div>

        <DecisionLane
          decisions={decisionTimeline.decisions}
          counts={decisionTimeline.counts}
          windowHours={decisionTimeline.windowHours}
          loading={decisionTimeline.loading}
          error={decisionTimeline.error}
        />

        <Accordion type="single" collapsible className="rounded-md border border-white/10 bg-black/20 px-3">
          <AccordionItem value="chart-data-details" className="border-none">
            <AccordionTrigger className="py-3 text-xs text-white/70 hover:no-underline">
              <div className="text-left">
                <div className="font-medium text-white/80">Data details</div>
                <div className="mt-0.5 text-[11px] text-white/45">
                  {displayedCandlePayload.count || 0} bars • coverage and indicator snapshot
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="grid gap-3 pb-3 text-xs text-white/55 sm:grid-cols-2">
              <div>
                <div className="font-medium uppercase tracking-[0.14em] text-white/45">Coverage Window</div>
                <div className="mt-2">{displayedCandlePayload.firstTimestamp ? formatChartTimestamp(displayedCandlePayload.firstTimestamp, chartTimeZone) : "n/a"}</div>
                <div>{displayedCandlePayload.lastTimestamp ? formatChartTimestamp(displayedCandlePayload.lastTimestamp, chartTimeZone) : "n/a"}</div>
              </div>
              <div>
                <div className="font-medium uppercase tracking-[0.14em] text-white/45">Indicator Snapshot</div>
                <div className="mt-2">Bars: {displayedCandlePayload.count || 0}</div>
                {strategyChartData.indicatorSnapshot?.kama ? <div>KAMA: {strategyChartData.indicatorSnapshot.kama}</div> : null}
                {strategyChartData.indicatorSnapshot?.atr ? <div>ATR: {strategyChartData.indicatorSnapshot.atr}</div> : null}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {renderMobileChartControlsDrawer()}
      </CardContent>
    </Card>
  );

  const renderMobileInsightsPanel = () => {
    let content = null;
    if (mobileInsightTab === "indicators") {
      content = (
        <IndicatorsTab
          indicators={indicators}
          rsiColor={rsiColor}
          rsiState={rsiState}
          trend={trend}
          trendColor={trendColor}
        />
      );
    } else if (mobileInsightTab === "signals") {
      content = <SignalsTab displayedSignals={displayedSignals} />;
    } else if (mobileInsightTab === "news") {
      content = (
        <NewsTab
          newsEvents={newsEvents}
          newsLoading={newsLoading}
          fetchNewsEvents={fetchNewsEvents}
          symbol={symbol}
          appTimeZone={appTimeZone}
        />
      );
    } else {
      content = (
        <AITab
          aiAnalysis={aiAnalysis}
          aiLoading={aiLoading}
          fetchAIAnalysis={fetchAIAnalysis}
        />
      );
    }

    return (
      <div className="space-y-4">
        <Card className="border-white/10 bg-white/[0.03]">
          <CardContent className="space-y-4 p-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">Insights</div>
              <div className="mt-1 text-sm font-semibold text-white">Signals, news, and AI analysis</div>
            </div>
            {renderMobileInsightTabs()}
            {content}
          </CardContent>
        </Card>

        <Accordion type="single" collapsible className="rounded-2xl border border-white/10 bg-white/[0.03] px-0">
          <AccordionItem value="market-pulse" className="border-none px-0">
            <AccordionTrigger className="px-4 py-4 text-left text-white hover:no-underline">
              <div>
                <div className="text-sm font-semibold text-white">Market Pulse</div>
                <div className="text-xs uppercase tracking-[0.18em] text-white/45">Sentiment and ladder view</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 px-3 pb-3">
              {renderSentimentAndLadder()}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  };

  const renderMobileActiveSection = () => {
    if (mobileSection === "chart") {
      return renderMobileChartWorkspace();
    }
    if (mobileSection === "trade") {
      return (
        <div className="space-y-4">
          {renderOrderEntryPanel(true)}
          {renderAutoTradingPanel()}
          {renderAccountSnapshotPanel()}
        </div>
      );
    }
    if (mobileSection === "positions") {
      return (
        <div className="space-y-4">
          {renderPositionsPanel()}
          {renderAccountSnapshotPanel()}
        </div>
      );
    }
    return renderMobileInsightsPanel();
  };

  const renderSentimentAndLadder = () => (
    <>
      <MarketSentiment symbol={symbol} />
      <PriceLadder
        symbol={symbol}
        contractId={contractMap[symbol] || CONTRACT_MAP[symbol] || null}
        bidPrice={bidPrice}
        askPrice={askPrice}
        currentPrice={currentPrice}
      />
    </>
  );

  const renderAccountSnapshotPanel = () => (
    <AccountSnapshot
      account={account}
      selectedTradingAccount={selectedTradingAccount}
      currentPrice={currentPrice}
      priceChange={priceChange}
      bidPrice={bidPrice}
      askPrice={askPrice}
      dayHigh={dayHigh}
      dayLow={dayLow}
      trend={trend}
      trendColor={trendColor}
      trendBg={trendBg}
      effectiveAccountBalance={effectiveAccountBalance}
      effectiveBuyingPower={effectiveBuyingPower}
      displayedDayPnl={displayedDayPnl}
      realtimeUnrealizedPnl={displayedUnrealizedPnl}
      selectedInstrumentOption={selectedInstrumentOption}
      symbol={symbol}
    />
  );

  const renderTradingTruthBar = (compact = false) => {
    const truthProps = {
      accountName: selectedTradingAccount?.account_name || "",
      accountActive: selectedTradingAccount?.is_active !== false,
      accountCanTrade: selectedTradingAccount?.can_trade !== false,
      brokerConnected: Boolean(selectedTradingAccount?.is_connected || account?.is_connected),
      brokerStateKnown: brokerState.state === "ok",
      symbol,
      contractLabel: selectedContractLabel,
      strategyName:
        activeAccountContractLink?.strategy_name
        || activeAccountContractLink?.strategy_name_snapshot
        || "",
      strategyTimeframe: activeAccountContractLink?.interval_minutes ?? null,
      chartTimeframe: timeframe,
      marketLive: Boolean(wsConnected),
      marketQuality: formatDataQualityLabel(chartDataQuality),
      autoTradeEnabled: autoTradeEnabledForSelectedAccount,
      inAutomationScope: selectedAccountIsInAutoTradeScope,
      hasActiveLink: selectedScopeHasActiveLink,
      ...selectedSymbolPositionTruth,
      latestDecision: {
        status: latestAutomationRun?.status,
        decision: latestAutomationRun?.summary?.decision,
        reason_code: latestAutomationRun?.summary?.reason_code,
      },
    };

    // The five-card grid cost ~90px to say very little; the dense strip says
    // the same in one row and expands on demand, giving that height to the
    // chart. Compact/mobile keeps the stacked summary, which suits a narrow
    // viewport better than a wrapping row of chips.
    if (!compact) {
      return <ChartStatusStrip {...truthProps} />;
    }

    return (
      <TradingTruthBar
        compact
        {...truthProps}
        className="grid-cols-2 [&>*:last-child]:col-span-2"
      />
    );
  };


  const renderTakeControlToggle = () => {
    if (!selectedTradingAccountId) return null;
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-white">TradoPro Takes Control</span>
              <p className="text-xs text-white/70 mt-0.5">Adopt &amp; manage positions opened externally</p>
            </div>
            <Switch
              checked={Boolean(adoptExternalTrades)}
              onCheckedChange={toggleAdoptExternalTrades}
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAutoTradingPanel = () => (
    <AutoTradingControls
      selectedTradingAccountId={selectedTradingAccountId}
      autoTradeEnabledForSelectedAccount={autoTradeEnabledForSelectedAccount}
      toggleAutoTrading={toggleAutoTrading}
      paperModeForSelectedScope={paperModeForSelectedScope}
      togglePaperMode={togglePaperMode}
      displayedPaperMode={displayedPaperMode}
      aiGuidedTradingEnabled={aiGuidedTradingEnabled}
      aiGuidedTradingAvailable={aiGuidedTradingAvailable}
      toggleAiGuidedTrading={toggleAiGuidedTrading}
      aiResearchInstruments={aiResearchInstruments}
      aiResearchAllowLimitOrders={aiResearchAllowLimitOrders}
      updateAiResearchSettings={updateAiResearchSettings}
      autoTradeEnabled={autoTradeEnabled}
      autoTradeAccountIds={autoTradeAccountIds}
      selectedAccountIsInAutoTradeScope={selectedAccountIsInAutoTradeScope}
      selectedScopeHasActiveLink={selectedScopeHasActiveLink}
      selectedInstrumentOption={selectedInstrumentOption}
      symbol={symbol}
      latestAutomationRun={latestAutomationRun}
      latestAutomationRunLoading={latestAutomationRunLoading}
      latestAutomationRunTime={latestAutomationRun?.created_at
        ? formatDateTimeDisplay(latestAutomationRun.created_at, appTimeZone, { timeZoneName: "short" })
        : null}
      goToAutoTradeSettings={goToAutoTradeSettings}
      goToStrategies={goToStrategies}
      boundIntervalMinutes={activeAccountContractLink?.interval_minutes ?? null}
      currentIntervalMinutes={selectedIntervalMinutes}
      activeLinkId={activeAccountContractLink?.id || null}
      contractsToTrade={activeAccountContractLink?.contracts_to_trade || 1}
      contractsToTradeSaving={savingAutoTradeContracts}
      onSaveContractsToTrade={handleSaveAutoTradeContracts}
    />
  );

  const renderOrderEntryPanel = (compact = false) => (
    <OrderEntryPanel
      selectedTradingAccount={selectedTradingAccount}
      account={account}
      timeZone={appTimeZone}
      selectedTradingAccountId={selectedTradingAccountId}
      activeAccountContractLink={activeAccountContractLink}
      symbol={symbol}
      compact={compact}
      orderEntry={orderEntry}
      orderDiagnostics={currentOrderDiagnostics}
      orderDiagnosticsLoading={orderDiagnosticsLoading}
      onPlaceOrder={handlePlaceOrder}
    />
  );

  const renderPositionsPanel = () => (
    <>
      {brokerState.state !== "ok" ? (
        <div className="mb-2 rounded border border-yellow-400/40 bg-yellow-500/10 px-3 py-2 text-[11px] text-yellow-200">
          <div className="font-semibold">
            {brokerState.state === "stale"
              ? "Fresh broker verification required before entry."
              : "Broker state unavailable — entry blocked."}
          </div>
          <div className="text-yellow-200/80">
            {brokerState.warning || "Showing last confirmed broker state."}
            {brokerState.lastConfirmedAt ? (
              <> Last confirmed: {new Date(brokerState.lastConfirmedAt).toLocaleString()}</>
            ) : (
              <> Last confirmed: unknown</>
            )}
          </div>
        </div>
      ) : null}
      <PositionsPanel
        positions={positions}
        closedPositions={trades}
        realtimePnls={realtimePositionPnls}
        onClose={closePosition}
        onFlatAll={flatAll}
        onSyncStop={handleSyncProtectiveStop}
        onReProtect={handleReProtectPosition}
        getDisplayedPositionPnl={resolvePositionPnl}
        timeZone={appTimeZone}
      />
    </>
  );

  const dashboardHeaderActions = isCompactLayout ? (
    <>
      {loading ? (
        <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
          <span className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Syncing
          </span>
        </Badge>
      ) : null}
      <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
        {wsConnected ? (
          <span className="flex items-center gap-1">
            <Wifi className="h-3 w-3 text-green-500" />
            Live
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <WifiOff className="h-3 w-3 text-yellow-500" />
            Delayed
          </span>
        )}
      </Badge>
      <Button
        size="sm"
        variant="outline"
        onClick={fetchMarketData}
        className="bg-white/5 border-white/10 text-white hover:bg-white/10"
      >
        Refresh
      </Button>
    </>
  ) : (
    <>
      {loading ? (
        <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
          <span className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Syncing
          </span>
        </Badge>
      ) : null}
      <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
        {wsConnected ? (
          <span className="flex items-center gap-1">
            <Wifi className="h-3 w-3 text-green-500" />
            Live
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <WifiOff className="h-3 w-3 text-yellow-500" />
            Delayed
          </span>
        )}
      </Badge>
      <Select value={symbol} onValueChange={handleManualSymbolChange}>
        <SelectTrigger className="w-[120px] bg-white/5 border-white/10 text-white">
          <SelectValue placeholder="Symbol" />
        </SelectTrigger>
        <SelectContent className="bg-black border-white/10">
          {availableInstrumentOptions.map((option) => (
            <SelectItem key={option.value} value={option.value} className="text-white focus:bg-white/10">
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={accountSelectValue}
        onValueChange={handleAccountChange}
        disabled={!tradingAccounts.length}
      >
        <SelectTrigger className="w-[250px] bg-white/5 border-white/10 text-white">
          <SelectValue placeholder="Select account" />
        </SelectTrigger>
        <SelectContent className="bg-black border-white/10">
          {!tradingAccounts.length ? (
            <SelectItem value="none" className="text-white focus:bg-white/10">
              No account connected
            </SelectItem>
          ) : (
            tradingAccounts.map((entry) => (
              <SelectItem key={entry.id} value={entry.id} className="text-white focus:bg-white/10">
                {entry.account_name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </>
  );

  return (
    <PlatformShell
      title="Trading Dashboard"
      titleIcon={BarChart3}
      backTo="/"
      currentKey="dashboard"
      mainClassName="h-[calc(100dvh-64px)] overflow-hidden"
      headerActions={isCompactLayout ? null : dashboardHeaderActions}
      extraRailActions={[
        { id: "refresh", label: "Refresh data", icon: RefreshCw, onClick: fetchMarketData },
      ]}
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden font-sans">
        <div className="hidden h-full min-h-0 lg:flex lg:flex-col">
          <div className="shrink-0 border-b border-white/10 px-4 py-3">
            {renderTradingTruthBar()}
          </div>
          <div className="min-h-0 flex-1">
            <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={70} minSize={50}>
              <div className="flex h-full flex-col">
                <div className="border-b border-white/10 px-4 py-2">
                  {renderDashboardTabs()}
                </div>
                <div className="flex-1 overflow-hidden p-4">
                  <DashboardSectionErrorBoundary resetKey={dashboardRenderBoundaryKey}>
                    {renderActiveDashboardTab()}
                  </DashboardSectionErrorBoundary>
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle className="bg-white/10 w-1 hover:bg-white/20" />

            <ResizablePanel defaultSize={30} minSize={25}>
              <div className="flex h-full flex-col">
                <ScrollArea className="flex-1">
                  <div className="space-y-4 p-4">
                    {renderSentimentAndLadder()}
                    {renderAccountSnapshotPanel()}
                    {renderTakeControlToggle()}
                    {renderAutoTradingPanel()}
                    {renderOrderEntryPanel()}
                    {renderPositionsPanel()}
                  </div>
                </ScrollArea>
              </div>
            </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </div>

        {renderMobileAccountBar()}
        <div
          data-testid="mobile-dashboard-scroll"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain lg:hidden"
        >
          <div className="space-y-3 p-3 pb-8">
            {renderMobileTradingContextCard()}
            {renderMobileSectionTabs()}
            <DashboardSectionErrorBoundary resetKey={mobileRenderBoundaryKey}>
              {renderMobileActiveSection()}
            </DashboardSectionErrorBoundary>
          </div>
        </div>
      </div>
    </PlatformShell>
  );
};

export default Dashboard;
